import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { ArrowLeft, User, Phone, Mail, Instagram, Image as ImageIcon, Info, Plus, Edit, Trash2, X, MapPin, Table, Map, FileText, ExternalLink, Download, CheckCircle, Lock, Users, Image as ImageLucide, Music, MoreVertical, Play } from 'lucide-react';
import { Person, StalkerLog } from '../../types';
import { stalkerAPI, getStaticFileUrl, getProxyUrl } from '../../services/api';
import { useDarkMode } from '../../contexts/DarkModeContext';
import { useAlert } from '../../contexts/AlertContext';
import { GENDER_MAP, SEXUAL_ORIENTATION_MAP, STATUS_MAP, PLATFORM_MAP } from '../../utils/enums';
import loggingService from '../../services/loggingService';

const PersonDetail: React.FC = () => {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { isDark } = useDarkMode();
	const { showAlert } = useAlert();
	const [person, setPerson] = useState<Person | null>(null);
	const [loading, setLoading] = useState(true);
	const [showNumberModal, setShowNumberModal] = useState(false);
	const [showEmailModal, setShowEmailModal] = useState(false);
	const [showMediaModal, setShowMediaModal] = useState(false);
	const [showSocialModal, setShowSocialModal] = useState(false);
	const [showLocationModal, setShowLocationModal] = useState(false);
	const [showPersonEditModal, setShowPersonEditModal] = useState(false);
	const [locationViewType, setLocationViewType] = useState<'table' | 'map'>('table');
	const [editingItem, setEditingItem] = useState<any>(null);
	const [mediaPreview, setMediaPreview] = useState<string | null>(null);
	const [mediaFile, setMediaFile] = useState<File | null>(null);
	const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
	const [avatarFile, setAvatarFile] = useState<File | null>(null);
	const [isDraggingMedia, setIsDraggingMedia] = useState(false);
	const [isDraggingAvatar, setIsDraggingAvatar] = useState(false);
	const [hideInHome, setHideInHome] = useState(false);
	const [lightboxImage, setLightboxImage] = useState<string | null>(null);
	const [lightboxType, setLightboxType] = useState<'image' | 'video'>('image');
	const [isAvatarLightbox, setIsAvatarLightbox] = useState(false);
	const [lightboxMedia, setLightboxMedia] = useState<any>(null);
	const [zoomLevel, setZoomLevel] = useState(1);
	const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
	const [isDragging, setIsDragging] = useState(false);
	const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
	const [stalkerLogs, setStalkerLogs] = useState<StalkerLog[]>([]);
	const [lastUpdate, setLastUpdate] = useState<string>('');
	const [avatarErrors, setAvatarErrors] = useState<Set<number>>(new Set());
	const [medias, setMedias] = useState<any[]>([]);
	const [mediaPage, setMediaPage] = useState(1);
	const [mediaLoading, setMediaLoading] = useState(false);
	const [mediaHasMore, setMediaHasMore] = useState(true);
	const [mediaTotalCount, setMediaTotalCount] = useState(0);
	const mediaObserverTarget = useRef<HTMLDivElement>(null);
	const hasLoggedPageView = useRef(false);

	const isInstagramLog = (log: StalkerLog) => (log.sl_platform || '').toLowerCase() === 'instagram';
	const isVSCOLog = (log: StalkerLog) => (log.sl_platform || '').toLowerCase() === 'vsco';
	const isTiktokLog = (log: StalkerLog) => (log.sl_platform || '').toLowerCase() === 'tiktok';

	const isInstagramSupportedChangeType = (changeType: string) => {
		const t = (changeType || '').toLowerCase();
		return [
			'followercount_changed',
			'followingcount_changed',
			'mediacount_changed',
			'profilepicurl_changed',
			'isprivate_changed',
			'name_changed',
			'bio_changed',
			'isverified_changed',
			'username_changed',
		].includes(t);
	};

	const isVSCOSupportedChangeType = (changeType: string) => {
		const t = (changeType || '').toLowerCase();
		return [
			'bio_changed',
			'avatarurl_changed',
			'profilelink_changed',
			'username_changed',
			'name_changed',
		].includes(t);
	};

	const isTiktokSupportedChangeType = (changeType: string) => {
		const t = (changeType || '').toLowerCase();
		return [
			'followercount_changed',
			'followingcount_changed',
			'videocount_changed',
			'avatarurl_changed',
			'isprivate_changed',
			'name_changed',
			'bio_changed',
			'language_changed',
			'isverified_changed',
			'username_changed',
			'tiktokid_changed',
			'secuid_changed',
		].includes(t);
	};

	const getFieldFromChangeType = (changeType: string) => {
		const t = (changeType || '').trim();
		if (!t.endsWith('_changed')) return null;
		return t.slice(0, -'_changed'.length);
	};

	const pickValue = (val: any, field: string | null) => {
		if (field && val && typeof val === 'object' && field in val) return val[field];
		return val;
	};

	const toNumberOrNull = (v: any): number | null => {
		if (v === null || v === undefined) return null;
		const n = typeof v === 'number' ? v : Number(v);
		return Number.isFinite(n) ? n : null;
	};

	const formatValue = (v: any) => {
		if (v === null || v === undefined) return '-';
		if (typeof v === 'number') return v.toLocaleString('tr-TR');
		return String(v);
	};

	useEffect(() => {
		if (id) {
			loadPerson();
			loadStalkerLogs();
		}
		// Reset log flag when id changes
		hasLoggedPageView.current = false;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id]);

	// Check for tab parameter in URL (Redirect to sections if needed in future, but for now we follow single page)
	useEffect(() => {
		const tabParam = searchParams.get('tab');
		if (tabParam) {
			const element = document.getElementById(`section-${tabParam}`);
			if (element) {
				element.scrollIntoView({ behavior: 'smooth' });
			}
		}
	}, [searchParams]);

	// Polling logs for updates (Simplified for single page)
	useEffect(() => {
		if (id) {
			const interval = setInterval(() => {
				loadStalkerLogs();
			}, 10000); // 10 seconds
			return () => clearInterval(interval);
		}
	}, [id]);

	// Load more medias
	const loadMoreMedias = async () => {
		if (mediaLoading || !mediaHasMore || !id) return;
		
		try {
			setMediaLoading(true);
			const nextPage = mediaPage + 1;
			const response = await stalkerAPI.persons.getMedia(Number(id), { 
				page: nextPage, 
				limit: 30 
			});
			const newMedias = response.data.data?.media || [];
			const totalCount = response.data.data?.pagination?.total || 0;
			if (newMedias.length > 0) {
				setMedias(prev => [...prev, ...newMedias]);
				setMediaPage(nextPage);
				setMediaTotalCount(totalCount);
				setMediaHasMore(newMedias.length === 30 && medias.length + newMedias.length < totalCount);
			} else {
				setMediaHasMore(false);
			}
		} catch (error) {
			console.error('Error loading more medias:', error);
			setMediaHasMore(false);
		} finally {
			setMediaLoading(false);
		}
	};

	// Infinite scroll for medias
	useEffect(() => {

		const observer = new IntersectionObserver(
			entries => {
				if (entries[0].isIntersecting && mediaHasMore && !mediaLoading) {
					loadMoreMedias();
				}
			},
			{ threshold: 0.1 }
		);

		const currentTarget = mediaObserverTarget.current;
		if (currentTarget) {
			observer.observe(currentTarget);
		}

		return () => {
			if (currentTarget) {
				observer.unobserve(currentTarget);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mediaHasMore, mediaLoading]);

	// Lock body scroll when lightbox is open
	useEffect(() => {
		if (lightboxImage) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = '';
		}
		return () => {
			document.body.style.overflow = '';
		};
	}, [lightboxImage]);

	const loadPerson = async () => {
		try {
			const response = await stalkerAPI.persons.getById(Number(id));
			const personData = response.data.data;
			setPerson(personData);
			setHideInHome(personData?.person_hideHome === '1');
			
			// Set media count and initial medias
			if (personData?.mediaCount !== undefined) {
				setMediaTotalCount(personData.mediaCount);
			}
			if (personData?.media && Array.isArray(personData.media)) {
				setMedias(personData.media);
				setMediaPage(1);
				setMediaHasMore(personData.media.length < (personData.mediaCount || 0));
			}
			
			// Update last update timestamp
			if (personData?.updated_at) {
				const updateDate = new Date(personData.updated_at);
				const day = String(updateDate.getDate()).padStart(2, '0');
				const month = String(updateDate.getMonth() + 1).padStart(2, '0');
				const year = updateDate.getFullYear();
				const hours = String(updateDate.getHours()).padStart(2, '0');
				const minutes = String(updateDate.getMinutes()).padStart(2, '0');
				const seconds = String(updateDate.getSeconds()).padStart(2, '0');
				setLastUpdate(`${day}.${month}.${year} - ${hours}:${minutes}:${seconds}`);
			}
			
			// Log page view with person name if available (only once)
			if (!hasLoggedPageView.current) {
				const personName = personData?.person_name && personData?.person_surname 
					? `${personData.person_name} ${personData.person_surname}`
					: personData?.person_name || personData?.person_surname || 'Person Detail';
				loggingService.logPageView(`/stalker/persons/${id}`, 'Stalker', personName);
				hasLoggedPageView.current = true;
			}
		} catch (error) {
			console.error('Error loading person:', error);
			// Log page view even if person load fails (only once)
			if (!hasLoggedPageView.current) {
				loggingService.logPageView(`/stalker/persons/${id}`, 'Stalker', 'Person Detail');
				hasLoggedPageView.current = true;
			}
		} finally {
			setLoading(false);
		}
	};

	const loadStalkerLogs = async () => {
		try {
			const response = await stalkerAPI.logs.getAll({ personId: Number(id), limit: 100 });
			setStalkerLogs(response.data.data);
		} catch (error) {
			console.error('Error loading stalker logs:', error);
		}
	};

	const instagramTitle = useCallback((log: StalkerLog) => {
		const changeType = (log.sl_changeType || '').toLowerCase();
		const field = getFieldFromChangeType(log.sl_changeType);
		const oldRaw = pickValue(log.sl_oldValue, field);
		const newRaw = pickValue(log.sl_newValue, field);

		if (changeType === 'followercount_changed') {
			const isDecrease = (toNumberOrNull(oldRaw) ?? 0) > (toNumberOrNull(newRaw) ?? 0);
			return isDecrease ? 'Follower Count ↓' : 'Follower Count ↑';
		}
		if (changeType === 'followingcount_changed') {
			const isDecrease = (toNumberOrNull(oldRaw) ?? 0) > (toNumberOrNull(newRaw) ?? 0);
			return isDecrease ? 'Following Count ↓' : 'Following Count ↑';
		}
		if (changeType === 'mediacount_changed') {
			const isDecrease = (toNumberOrNull(oldRaw) ?? 0) > (toNumberOrNull(newRaw) ?? 0);
			return isDecrease ? 'Media Count ↓' : 'Media Count ↑';
		}
		if (changeType === 'profilepicurl_changed') return 'Profile Picture Changed';
		if (changeType === 'isprivate_changed') {
			const oldV = String(oldRaw ?? '');
			const newV = String(newRaw ?? '');
			if (oldV === '0' && newV === '1') return 'Profile Locked';
			if (oldV === '1' && newV === '0') return 'Profile Unlocked';
			return 'Profile Privacy Changed';
		}
		if (changeType === 'name_changed') return 'Profile Name Changed';
		if (changeType === 'bio_changed') return 'Profile Bio Changed';
		if (changeType === 'isverified_changed') {
			const oldV = String(oldRaw ?? '');
			const newV = String(newRaw ?? '');
			if (oldV === '1' && newV === '0') return 'Profile Unverified';
			if (oldV === '0' && newV === '1') return 'Profile Verified';
			return 'Profile Verification Changed';
		}
		return log.sl_changeType;
	}, []);

	const tiktokTitle = useCallback((log: StalkerLog) => {
		const changeType = (log.sl_changeType || '').toLowerCase();
		const field = getFieldFromChangeType(log.sl_changeType);
		const oldRaw = pickValue(log.sl_oldValue, field);
		const newRaw = pickValue(log.sl_newValue, field);

		if (changeType === 'followercount_changed') {
			const isDecrease = (toNumberOrNull(oldRaw) ?? 0) > (toNumberOrNull(newRaw) ?? 0);
			return isDecrease ? 'Follower Count ↓' : 'Follower Count ↑';
		}
		if (changeType === 'followingcount_changed') {
			const isDecrease = (toNumberOrNull(oldRaw) ?? 0) > (toNumberOrNull(newRaw) ?? 0);
			return isDecrease ? 'Following Count ↓' : 'Following Count ↑';
		}
		if (changeType === 'videocount_changed') {
			const isDecrease = (toNumberOrNull(oldRaw) ?? 0) > (toNumberOrNull(newRaw) ?? 0);
			return isDecrease ? 'Media Count ↓' : 'Media Count ↑';
		}
		if (changeType === 'avatarurl_changed') return 'Profile Picture Changed';
		if (changeType === 'isprivate_changed') {
			const oldV = String(oldRaw ?? '');
			const newV = String(newRaw ?? '');
			if (oldV === '0' && newV === '1') return 'Profile Locked';
			if (oldV === '1' && newV === '0') return 'Profile Unlocked';
			return 'Profile Privacy Changed';
		}
		if (changeType === 'username_changed') return 'Profile Username Changed';
		if (changeType === 'name_changed') return 'Profile Name Changed';
		if (changeType === 'bio_changed') return 'Profile Bio Changed';
		if (changeType === 'isverified_changed') {
			const oldV = String(oldRaw ?? '');
			const newV = String(newRaw ?? '');
			if (oldV === '1' && newV === '0') return 'Profile Unverified';
			if (oldV === '0' && newV === '1') return 'Profile Verified';
			return 'Profile Verification Changed';
		}
		return log.sl_changeType;
	}, []);

	const vscoTitle = useCallback((log: StalkerLog) => {
		const changeType = (log.sl_changeType || '').toLowerCase();
		const field = getFieldFromChangeType(log.sl_changeType);

		if (changeType === 'avatarurl_changed') return 'Profile Picture Changed';
		if (changeType === 'username_changed') return 'Profile Username Changed';
		if (changeType === 'name_changed') return 'Profile Name Changed';
		if (changeType === 'bio_changed') return 'Profile Bio Changed';
		if (changeType === 'profilelink_changed') return 'Profile Link Changed';
		return log.sl_changeType;
	}, []);

	const instagramDescription = (log: StalkerLog) => {
		const changeType = (log.sl_changeType || '').toLowerCase();
		const field = getFieldFromChangeType(log.sl_changeType);
		const oldRaw = pickValue(log.sl_oldValue, field);
		const newRaw = pickValue(log.sl_newValue, field);

		if (['followercount_changed', 'followingcount_changed', 'mediacount_changed'].includes(changeType)) {
			const label =
				changeType === 'followercount_changed'
					? 'Profile Followers'
					: changeType === 'followingcount_changed'
					? 'Profile Followings'
					: 'Profile Medias';
			return `${label}: ${formatValue(oldRaw)} -> ${formatValue(newRaw)}`;
		}

		if (changeType === 'profilepicurl_changed') {
			return 'Profile picture was updated.';
		}

		if (changeType === 'isprivate_changed') {
			const oldV = String(oldRaw ?? '');
			const newV = String(newRaw ?? '');
			const oldLocked = oldV === '1';
			const newLocked = newV === '1';
			return `Profile: ${oldLocked ? 'Private' : 'Public'} -> ${newLocked ? 'Private' : 'Public'}`;
		}

		if (changeType === 'name_changed') {
			return `Profile Name: ${formatValue(oldRaw)} -> ${formatValue(newRaw)}`;
		}

		if (changeType === 'bio_changed') {
			return `Profile Bio: ${formatValue(oldRaw)} -> ${formatValue(newRaw)}`;
		}

		if (changeType === 'isverified_changed') {
			const oldV = String(oldRaw ?? '');
			const newV = String(newRaw ?? '');
			const oldVerified = oldV === '1';
			const newVerified = newV === '1';
			return `Profile Verification: ${oldVerified ? 'Verified' : 'Unverified'} -> ${
				newVerified ? 'Verified' : 'Unverified'
			}`;
		}

		if (changeType === 'username_changed') {
			return `Profile Username: ${formatValue(oldRaw)} -> ${formatValue(newRaw)}`;
		}

		return log.sl_description || '';
	};

	const tiktokDescription = (log: StalkerLog) => {
		const changeType = (log.sl_changeType || '').toLowerCase();
		const field = getFieldFromChangeType(log.sl_changeType);
		const oldRaw = pickValue(log.sl_oldValue, field);
		const newRaw = pickValue(log.sl_newValue, field);

		if (['followercount_changed', 'followingcount_changed', 'videocount_changed'].includes(changeType)) {
			const label =
				changeType === 'followercount_changed'
					? 'Profile Followers'
					: changeType === 'followingcount_changed'
					? 'Profile Followings'
					: 'Profile Medias';
			return `${label}: ${formatValue(oldRaw)} -> ${formatValue(newRaw)}`;
		}

		if (changeType === 'avatarurl_changed') {
			return 'Profile picture was updated.';
		}

		if (changeType === 'isprivate_changed') {
			const oldV = String(oldRaw ?? '');
			const newV = String(newRaw ?? '');
			const oldLocked = oldV === '1';
			const newLocked = newV === '1';
			return `Profile: ${oldLocked ? 'Private' : 'Public'} -> ${newLocked ? 'Private' : 'Public'}`;
		}

		if (changeType === 'name_changed') {
			return `Profile Name: ${formatValue(oldRaw)} -> ${formatValue(newRaw)}`;
		}

		if (changeType === 'bio_changed') {
			return `Profile Bio: ${formatValue(oldRaw)} -> ${formatValue(newRaw)}`;
		}

		if (changeType === 'isverified_changed') {
			const oldV = String(oldRaw ?? '');
			const newV = String(newRaw ?? '');
			const oldVerified = oldV === '1';
			const newVerified = newV === '1';
			return `Profile Verification: ${oldVerified ? 'Verified' : 'Unverified'} -> ${
				newVerified ? 'Verified' : 'Unverified'
			}`;
		}

		if (changeType === 'username_changed') {
			return `Profile Username: ${formatValue(oldRaw)} -> ${formatValue(newRaw)}`;
		}

		return log.sl_description || '';
	};

	const vscoDescription = (log: StalkerLog) => {
		const changeType = (log.sl_changeType || '').toLowerCase();
		const field = getFieldFromChangeType(log.sl_changeType);
		const oldRaw = pickValue(log.sl_oldValue, field);
		const newRaw = pickValue(log.sl_newValue, field);

		if (changeType === 'bio_changed') {
			return `Profile Bio: ${formatValue(oldRaw)} -> ${formatValue(newRaw)}`;
		}
		if (changeType === 'avatarurl_changed') {
			return 'Profile picture was updated.';
		}
		if (changeType === 'profilelink_changed') {
			return `Profile Link: ${formatValue(oldRaw)} -> ${formatValue(newRaw)}`;
		}
		if (changeType === 'username_changed') {
			return `Profile Username: ${formatValue(oldRaw)} -> ${formatValue(newRaw)}`;
		}
		if (changeType === 'name_changed') {
			return `Profile Name: ${formatValue(oldRaw)} -> ${formatValue(newRaw)}`;
		}

		return log.sl_description || '';
	};

	const formatDate = (dateString: string | undefined): string => {
		if (!dateString) return '';
		const date = new Date(dateString);
		const day = String(date.getDate()).padStart(2, '0');
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const year = date.getFullYear();
		return `${day}.${month}.${year}`;
	};

	const calculateAge = (birthdate: string | undefined): number => {
		if (!birthdate) return 0;
		const today = new Date();
		const birth = new Date(birthdate);
		let age = today.getFullYear() - birth.getFullYear();
		const monthDiff = today.getMonth() - birth.getMonth();
		if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
			age--;
		}
		return age;
	};

	const daysUntilBirthday = (birthdate: string | undefined): number => {
		if (!birthdate) return 0;
		const today = new Date();
		const birth = new Date(birthdate);
		const nextBirthday = new Date(today.getFullYear(), birth.getMonth(), birth.getDate());
		if (nextBirthday < today) {
			nextBirthday.setFullYear(today.getFullYear() + 1);
		}
		const diffTime = nextBirthday.getTime() - today.getTime();
		return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
	};

	const handleDeleteNumber = async (numberId: number) => {
		if (!window.confirm('Are you sure you want to delete this number?')) return;
		try {
			const number = person?.numbers?.find(n => n.pn_id === numberId);
			await stalkerAPI.numbers.delete(numberId);
			if (number) {
				loggingService.logDelete('number', numberId, number.pn_number || 'Unknown', 'Stalker');
			}
			loadPerson();
		} catch (error) {
			console.error('Error deleting number:', error);
		}
	};

	const handleDeleteEmail = async (emailId: number) => {
		if (!window.confirm('Are you sure you want to delete this email?')) return;
		try {
			const email = person?.mails?.find(e => e.pm_id === emailId);
			await stalkerAPI.emails.delete(emailId);
			if (email) {
				loggingService.logDelete('email', emailId, email.pm_mail || 'Unknown', 'Stalker');
			}
			loadPerson();
		} catch (error) {
			console.error('Error deleting email:', error);
		}
	};

	const handleDeleteMedia = async (mediaId: number) => {
		if (!window.confirm('Are you sure you want to delete this media?')) return;
		try {
			const media = medias.find(m => m.sm_id === mediaId) || person?.media?.find(m => m.sm_id === mediaId);
			await stalkerAPI.medias.delete(mediaId);
			if (media) {
				loggingService.logDelete('media', mediaId, media.sm_fileName || 'Unknown', 'Stalker');
			}
			// Update medias state immediately
			setMedias(prev => prev.filter(m => m.sm_id !== mediaId));
			setMediaTotalCount(prev => Math.max(0, prev - 1));
			// Reload person to sync all data
			loadPerson();
		} catch (error) {
			console.error('Error deleting media:', error);
		}
	};

	const handleDeleteLocation = async (locationId: number) => {
		if (!window.confirm('Are you sure you want to delete this location?')) return;
		try {
			const location = person?.locations?.find(l => l.pl_id === locationId);
			await stalkerAPI.locations.delete(locationId);
			if (location) {
				loggingService.logDelete('location', locationId, location.pl_location || 'Unknown', 'Stalker');
			}
			loadPerson();
		} catch (error) {
			console.error('Error deleting location:', error);
		}
	};

	const handleLocationSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		const form = e.target as HTMLFormElement;
		const formData = new FormData(form);
		const data = {
			pl_personId: Number(id),
			pl_locationTitle: formData.get('pl_locationTitle') as string || '',
			pl_location: formData.get('pl_location') as string,
			pl_status: formData.get('pl_status') as string || 'active'
		};

		try {
			if (editingItem) {
				await stalkerAPI.locations.update(editingItem.pl_id, data);
				loggingService.logEdit('location', editingItem.pl_id, data.pl_location || 'Unknown', 'Stalker');
			} else {
				const response = await stalkerAPI.locations.create(data);
				const locationId = response.data.data?.pl_id;
				if (locationId) {
					loggingService.logCreate('location', locationId, data.pl_location || 'Unknown', 'Stalker');
				}
			}
			setShowLocationModal(false);
			setEditingItem(null);
			loadPerson();
		} catch (error) {
			console.error('Error saving location:', error);
		}
	};

	const handlePersonEditSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		const form = e.target as HTMLFormElement;
		const formData = new FormData(form);
		
		const data = new FormData();
		if (formData.get('person_pin')) data.append('person_pin', formData.get('person_pin') as string);
		if (formData.get('person_name')) data.append('person_name', formData.get('person_name') as string);
		if (formData.get('person_surname')) data.append('person_surname', formData.get('person_surname') as string);
		if (formData.get('person_gender')) data.append('person_gender', formData.get('person_gender') as string);
		if (formData.get('person_sexualOrientation')) data.append('person_sexualOrientation', formData.get('person_sexualOrientation') as string);
		if (formData.get('person_birthdate')) data.append('person_birthdate', formData.get('person_birthdate') as string);
		if (formData.get('person_birthCity')) data.append('person_birthCity', formData.get('person_birthCity') as string);
		if (formData.get('person_birthCountry')) data.append('person_birthCountry', formData.get('person_birthCountry') as string);
		data.append('person_hideHome', hideInHome ? '1' : '0');
		if (avatarFile) {
			data.append('person_avatar', avatarFile);
		}

		try {
			const response = await stalkerAPI.persons.update(Number(id), data);
			loggingService.logEdit('person', Number(id), `${person?.person_name} ${person?.person_surname}`, 'Stalker');
			
			// If avatar was updated, update the person state immediately with new avatar
			if (avatarFile && response.data.data?.person_avatar) {
				setPerson(prev => prev ? {
					...prev,
					person_avatar: response.data.data.person_avatar
				} : null);
			}
			
			setShowPersonEditModal(false);
			setAvatarPreview(null);
			setAvatarFile(null);
			loadPerson();
		} catch (error) {
			console.error('Error updating person:', error);
		}
	};

	const handleAvatarFile = (file: File) => {
		if (file) {
			setAvatarFile(file);
			const reader = new FileReader();
			reader.onloadend = () => {
				setAvatarPreview(reader.result as string);
			};
			reader.readAsDataURL(file);
		}
	};

	const formatPhoneNumber = (phone: string): string => {
		// Remove all non-digit characters
		const digits = phone.replace(/\D/g, '');
		// Format as 0555 555 55 55 (Turkish format)
		if (digits.length === 11 && digits.startsWith('0')) {
			return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 9)} ${digits.slice(9)}`;
		} else if (digits.length === 10) {
			return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8)}`;
		}
		// Return original if format doesn't match
		return phone;
	};

	const handleSocialSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		const form = e.target as HTMLFormElement;
		const formData = new FormData(form);
		const platform = formData.get('platform') as string;
		
		const data: any = {
			[platform === 'instagram' ? 'iu_personId' : platform === 'vsco' ? 'vu_personId' : platform === 'tiktok' ? 'tu_personId' : platform === 'x' || platform === 'twitter' ? 'xu_personId' : platform === 'snapchat' ? 'su_personId' : 'su_personId']: Number(id),
			[platform === 'instagram' ? 'iu_username' : platform === 'vsco' ? 'vu_username' : platform === 'tiktok' ? 'tu_username' : platform === 'x' || platform === 'twitter' ? 'xu_username' : platform === 'snapchat' ? 'su_username' : 'su_username']: formData.get('username') as string
		};

		// Add platform-specific fields
		if (platform === 'vsco') {
			if (formData.get('vu_name')) data.vu_name = formData.get('vu_name') as string;
		} else if (platform === 'tiktok') {
			if (formData.get('tu_name')) data.tu_name = formData.get('tu_name') as string;
		} else if (platform === 'x' || platform === 'twitter') {
			if (formData.get('xu_name')) data.xu_name = formData.get('xu_name') as string;
		} else if (platform === 'snapchat') {
			if (formData.get('su_name')) data.su_name = formData.get('su_name') as string;
		} else if (platform === 'spotify') {
			if (formData.get('su_name')) data.su_name = formData.get('su_name') as string;
		}

		try {
			const response = await stalkerAPI.socials.create(platform, data);
			const socialId = response.data.data?.[platform === 'instagram' ? 'iu_id' : platform === 'vsco' ? 'vu_id' : platform === 'tiktok' ? 'tu_id' : platform === 'x' || platform === 'twitter' ? 'xu_id' : platform === 'snapchat' ? 'su_id' : 'su_id'];
			if (socialId) {
				const username = formData.get('username') as string;
				const personName = person?.person_name && person?.person_surname 
					? `${person.person_name} ${person.person_surname}`
					: person?.person_name || person?.person_surname || 'Unknown';
				await loggingService.log({
					action: 'social_create',
					description: `Social created with ${username} for ${personName}`,
					metadata: {
						entity_id: socialId,
						entity_name: username,
						person_name: personName
					},
					log_api_type: 'Stalker'
				} as any);
			}
			setShowSocialModal(false);
			setEditingItem(null);
			loadPerson();
			
			// Show notification for Instagram and TikTok
			if (platform === 'instagram' || platform === 'tiktok') {
				showAlert('info', 'Social account added, data is being fetched. Please wait...');
			}
		} catch (error) {
			console.error('Error saving social:', error);
			showAlert('error', 'Failed to add social account');
		}
	};

	const handleNumberSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		const form = e.target as HTMLFormElement;
		const formData = new FormData(form);
		const data = {
			pn_personId: Number(id),
			pn_number: formData.get('pn_number') as string,
			pn_status: formData.get('pn_status') as string || 'active'
		};

		try {
			if (editingItem) {
				await stalkerAPI.numbers.update(editingItem.pn_id, data);
				loggingService.logEdit('number', editingItem.pn_id, data.pn_number, 'Stalker');
			} else {
				const response = await stalkerAPI.numbers.create(data);
				const numberId = response.data.data?.pn_id;
				if (numberId) {
					loggingService.logCreate('number', numberId, data.pn_number, 'Stalker');
				}
			}
			setShowNumberModal(false);
			setEditingItem(null);
			loadPerson();
		} catch (error) {
			console.error('Error saving number:', error);
		}
	};

	const handleEmailSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		const form = e.target as HTMLFormElement;
		const formData = new FormData(form);
		const data = {
			pm_personId: Number(id),
			pm_mail: formData.get('pm_mail') as string,
			pm_status: formData.get('pm_status') as string || 'active'
		};

		try {
			if (editingItem) {
				await stalkerAPI.emails.update(editingItem.pm_id, data);
				loggingService.logEdit('email', editingItem.pm_id, data.pm_mail, 'Stalker');
			} else {
				const response = await stalkerAPI.emails.create(data);
				const emailId = response.data.data?.pm_id;
				if (emailId) {
					loggingService.logCreate('email', emailId, data.pm_mail, 'Stalker');
				}
			}
			setShowEmailModal(false);
			setEditingItem(null);
			loadPerson();
		} catch (error) {
			console.error('Error saving email:', error);
		}
	};

	const handleMediaSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		const form = e.target as HTMLFormElement;
		const formData = new FormData(form);
		
		if (!editingItem && (!mediaFile || mediaFile.size === 0)) {
			alert('Please select a file to upload');
			return;
		}

		const data = new FormData();
		data.append('sm_personId', String(id));
		data.append('sm_platform', formData.get('sm_platform') as string);
		data.append('sm_mediaType', formData.get('sm_mediaType') as string || 'image');
		if (mediaFile) {
			data.append('sm_file', mediaFile);
		}
		if (formData.get('sm_description')) {
			data.append('sm_description', formData.get('sm_description') as string);
		}

		try {
			if (editingItem) {
				await stalkerAPI.medias.update(editingItem.sm_id, data);
				loggingService.logEdit('media', editingItem.sm_id, editingItem.sm_fileName || 'Unknown', 'Stalker');
			} else {
				const response = await stalkerAPI.medias.create(data);
				const mediaId = response.data.data?.sm_id;
				if (mediaId) {
					loggingService.logCreate('media', mediaId, mediaFile?.name || 'Unknown', 'Stalker');
				}
			}
			setShowMediaModal(false);
			setEditingItem(null);
			setMediaPreview(null);
			setMediaFile(null);
			loadPerson();
		} catch (error) {
			console.error('Error saving media:', error);
		}
	};

	const handleMediaFile = (file: File) => {
		if (file) {
			setMediaFile(file);
			const reader = new FileReader();
			reader.onloadend = () => {
				setMediaPreview(reader.result as string);
			};
			reader.readAsDataURL(file);
		}
	};

	if (loading) {
		return (
			<div className={`flex flex-col items-center justify-center h-64 font-mono text-xs ${isDark ? 'text-emerald-500' : 'text-emerald-600'}`}>
				<div className="flex items-center space-x-2">
					<div className="w-1 h-3 bg-current animate-pulse" />
					<span>[ FETCHING_INTEL_DATA... ]</span>
				</div>
			</div>
		);
	}

	if (!person) {
		return (
			<div className="text-center py-12 font-mono">
				<h3 className={`text-sm uppercase tracking-widest mb-2 ${isDark ? 'text-red-500' : 'text-red-600'}`}>[ ENTITY_NOT_FOUND ]</h3>
				<button
					onClick={() => navigate('/stalker/persons')}
					className={`mt-4 px-4 py-2 text-[10px] uppercase tracking-widest border transition-colors ${
						isDark ? 'border-neutral-700 text-neutral-400 hover:text-white hover:bg-neutral-800' : 'border-neutral-300 text-neutral-600 hover:text-black hover:bg-neutral-100'
					}`}
				>
					RETURN_TO_DATABASE
				</button>
			</div>
		);
	}

	return (
		<div className="space-y-4 px-2 md:px-0 pb-20">
			{/* Header */}
			<div className={`flex flex-col md:flex-row items-start justify-between md:items-center p-4 border rounded-none ${isDark ? 'bg-[#111] border-[#222]' : 'bg-white border-neutral-200'} gap-4`}>
				<div className="flex items-center space-x-4">
					<button
						onClick={() => navigate('/stalker/persons')}
						className={`p-1.5 border transition-colors ${isDark ? 'border-[#333] hover:bg-[#1a1a1a] text-neutral-500 hover:text-white' : 'border-neutral-200 hover:bg-neutral-100 text-neutral-500 hover:text-black'}`}
					>
						<ArrowLeft className="w-4 h-4" />
					</button>
					<div className="flex items-center space-x-6">
						{person.person_avatar ? (
							<img
								src={getStaticFileUrl(person.person_avatar) || ''}
								alt="AVT"
								className={`w-20 h-20 object-cover border cursor-pointer hover:opacity-80 transition-all ${isDark ? 'border-[#444]' : 'border-neutral-300'}`}
								onClick={() => {
									setLightboxImage(getStaticFileUrl(person.person_avatar) || '');
									setLightboxType('image');
									setIsAvatarLightbox(true);
									setLightboxMedia(null);
									setZoomLevel(1);
									setImagePosition({ x: 0, y: 0 });
								}}
							/>
						) : (
							<div className={`w-20 h-20 flex items-center justify-center font-mono text-2xl border ${isDark ? 'bg-[#1a1a1a] text-neutral-500 border-[#333]' : 'bg-neutral-100 text-neutral-500 border-neutral-300'}`}>
								{(person.person_name || '').charAt(0)}{(person.person_surname || '').charAt(0)}
							</div>
						)}
						<div className="flex-1">
							<h1 className={`text-xl font-mono font-bold tracking-widest uppercase ${isDark ? 'text-neutral-200' : 'text-neutral-800'}`}>
								{person.person_name} {person.person_surname}
							</h1>
							<p className={`text-[12px] font-mono mt-1 ${isDark ? 'text-emerald-500/70' : 'text-emerald-600/70'}`}>
								ID: {person.person_id.toString().padStart(6, '0')} | PIN: {person.person_pin || 'UNKNOWN'}
							</p>
						</div>
					</div>
				</div>

				<div className="flex items-center space-x-2 self-end md:self-auto">
					<button
						onClick={() => setShowPersonEditModal(true)}
						className={`flex items-center space-x-1.5 px-3 py-1.5 text-[10px] font-mono tracking-wider uppercase border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 transition-colors ${isDark ? 'bg-[#0a0a0a]' : 'bg-emerald-50'}`}
					>
						<Edit className="w-3 h-3" />
						<span>MODIFY</span>
					</button>
					<button
						onClick={async () => {
							if (!window.confirm(`Are you sure you want to delete ${person?.person_name} ${person?.person_surname}? This action cannot be undone.`)) return;
							try {
								await stalkerAPI.persons.delete(Number(id));
								if (person) {
									loggingService.logDelete('person', Number(id), `${person.person_name} ${person.person_surname}`, 'Stalker');
								}
								navigate('/stalker/persons');
							} catch (error) {
								console.error('Error deleting person:', error);
								showAlert('error', 'Failed to delete person');
							}
						}}
						className={`flex items-center space-x-1.5 px-3 py-1.5 text-[10px] font-mono tracking-wider uppercase border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors ${isDark ? 'bg-[#0a0a0a]' : 'bg-red-50'}`}
					>
						<Trash2 className="w-3 h-3" />
						<span>PURGE</span>
					</button>
				</div>
			</div>

			{/* Intel Dossier Views */}
			<div id="section-info" className={`border p-4 ${isDark ? 'bg-[#111] border-[#222]' : 'bg-white border-neutral-200'}`}>
				<h2 className={`text-[10px] font-mono tracking-widest uppercase mb-4 pb-2 border-b ${isDark ? 'text-emerald-500 border-[#222]' : 'text-emerald-700 border-neutral-200'}`}>
					// BASIC_INFO
				</h2>
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 font-mono text-[10px] uppercase">
					<div className={`p-3 border ${isDark ? 'bg-[#0a0a0a] border-[#222]' : 'bg-neutral-50 border-neutral-200'}`}>
						<span className={`${isDark ? 'text-neutral-500' : 'text-neutral-400'} block mb-1`}>GENDER</span>
						<span className={isDark ? 'text-neutral-200' : 'text-neutral-800'}>{person.person_gender ? GENDER_MAP[person.person_gender as keyof typeof GENDER_MAP] || person.person_gender : 'UNKNOWN'}</span>
					</div>
					<div className={`p-3 border ${isDark ? 'bg-[#0a0a0a] border-[#222]' : 'bg-neutral-50 border-neutral-200'}`}>
						<span className={`${isDark ? 'text-neutral-500' : 'text-neutral-400'} block mb-1`}>ORIENTATION</span>
						<span className={isDark ? 'text-neutral-200' : 'text-neutral-800'}>{person.person_sexualOrientation ? SEXUAL_ORIENTATION_MAP[person.person_sexualOrientation as keyof typeof SEXUAL_ORIENTATION_MAP] || person.person_sexualOrientation : 'UNKNOWN'}</span>
					</div>
					<div className={`p-3 border ${isDark ? 'bg-[#0a0a0a] border-[#222]' : 'bg-neutral-50 border-neutral-200'}`}>
						<span className={`${isDark ? 'text-neutral-500' : 'text-neutral-400'} block mb-1`}>BIRTHDATE</span>
						<span 
							className={isDark ? 'text-neutral-200' : 'text-neutral-800'}
							title={`${daysUntilBirthday(person.person_birthdate)} days until next birthday`}
						>
							{person.person_birthdate ? `${formatDate(person.person_birthdate)} [AGE:${calculateAge(person.person_birthdate)}]` : 'UNKNOWN'}
						</span>
					</div>
					<div className={`p-3 border ${isDark ? 'bg-[#0a0a0a] border-[#222]' : 'bg-neutral-50 border-neutral-200'}`}>
						<span className={`${isDark ? 'text-neutral-500' : 'text-neutral-400'} block mb-1`}>ORIGIN</span>
						<span className={isDark ? 'text-neutral-200' : 'text-neutral-800'}>
							{person.person_birthCity || person.person_birthCountry ? `${person.person_birthCity || '?'}, ${person.person_birthCountry || '?'}` : 'UNKNOWN'}
						</span>
					</div>
				</div>
			</div>

			{/* Numbers Module */}
			<div id="section-numbers" className={`mt-4 border p-4 ${isDark ? 'bg-[#111] border-[#222]' : 'bg-white border-neutral-200'}`}>
				<div className={`flex justify-between items-center mb-4 pb-2 border-b ${isDark ? 'border-[#222]' : 'border-neutral-200'}`}>
					<h2 className={`text-[10px] font-mono tracking-widest uppercase ${isDark ? 'text-emerald-500' : 'text-emerald-700'}`}>
						// COMMS: NUMBERS
					</h2>
					<button
						onClick={() => {
							setEditingItem(null);
							setShowNumberModal(true);
						}}
						className={`flex items-center space-x-1.5 px-3 py-1.5 text-[10px] font-mono tracking-wider uppercase border text-emerald-500 hover:bg-emerald-500/10 transition-colors ${isDark ? 'border-[#333] bg-[#0a0a0a]' : 'border-neutral-300 bg-neutral-50'}`}
					>
						<Plus className="w-3 h-3" />
						<span>ADD NUMBER</span>
					</button>
				</div>
				<div className="space-y-2">
					{person.numbers && person.numbers.length > 0 ? (
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
							{person.numbers.map((number) => (
								<div key={number.pn_id} className={`p-3 border flex flex-col justify-between ${isDark ? 'bg-[#0a0a0a] border-[#222]' : 'bg-neutral-50 border-neutral-200'}`}>
									<div className="mb-3">
										<p className={`font-mono text-sm tracking-widest ${isDark ? 'text-neutral-200' : 'text-neutral-800'}`}>{formatPhoneNumber(number.pn_number)}</p>
										<p className={`text-[10px] font-mono mt-1 ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>STATUS: {STATUS_MAP[number.pn_status] || number.pn_status}</p>
									</div>
									<div className="flex items-center space-x-2 self-end">
										<button
											onClick={() => {
												setEditingItem(number);
												setShowNumberModal(true);
											}}
											className={`p-1.5 border transition-colors ${isDark ? 'border-[#333] hover:bg-[#1a1a1a] text-neutral-500 hover:text-white' : 'border-neutral-200 hover:bg-neutral-100 text-neutral-500 hover:text-black'}`}
										>
											<Edit className="w-3 h-3" />
										</button>
										<button
											onClick={() => handleDeleteNumber(number.pn_id)}
											className={`p-1.5 border transition-colors ${isDark ? 'border-[#333] hover:bg-red-500/10 text-red-500 hover:text-red-400' : 'border-neutral-200 hover:bg-red-50 text-red-500 hover:text-red-600'}`}
										>
											<Trash2 className="w-3 h-3" />
										</button>
									</div>
								</div>
							))}
						</div>
					) : (
						<p className={`font-mono text-[10px] ${isDark ? 'text-neutral-600' : 'text-neutral-400'}`}>[ NO_PHONE_RECORDS_FOUND ]</p>
					)}
				</div>
			</div>

			{/* Emails Module */}
			<div id="section-emails" className={`mt-4 border p-4 ${isDark ? 'bg-[#111] border-[#222]' : 'bg-white border-neutral-200'}`}>
				<div className={`flex justify-between items-center mb-4 pb-2 border-b ${isDark ? 'border-[#222]' : 'border-neutral-200'}`}>
					<h2 className={`text-[10px] font-mono tracking-widest uppercase ${isDark ? 'text-emerald-500' : 'text-emerald-700'}`}>
						// COMMS: EMAILS
					</h2>
					<button
						onClick={() => {
							setEditingItem(null);
							setShowEmailModal(true);
						}}
						className={`flex items-center space-x-1.5 px-3 py-1.5 text-[10px] font-mono tracking-wider uppercase border text-emerald-500 hover:bg-emerald-500/10 transition-colors ${isDark ? 'border-[#333] bg-[#0a0a0a]' : 'border-neutral-300 bg-neutral-50'}`}
					>
						<Plus className="w-3 h-3" />
						<span>ADD EMAIL</span>
					</button>
				</div>
				<div className="space-y-2">
					{person.mails && person.mails.length > 0 ? (
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
							{person.mails.map((mail) => (
								<div key={mail.pm_id} className={`p-3 border flex flex-col justify-between ${isDark ? 'bg-[#0a0a0a] border-[#222]' : 'bg-neutral-50 border-neutral-200'}`}>
									<div className="mb-3">
										<p className={`font-mono text-xs ${isDark ? 'text-neutral-200' : 'text-neutral-800'}`}>{mail.pm_mail}</p>
										<p className={`text-[10px] font-mono mt-1 ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>STATUS: {STATUS_MAP[mail.pm_status] || mail.pm_status}</p>
									</div>
									<div className="flex items-center space-x-2 self-end">
										<button
											onClick={() => {
												setEditingItem(mail);
												setShowEmailModal(true);
											}}
											className={`p-1.5 border transition-colors ${isDark ? 'border-[#333] hover:bg-[#1a1a1a] text-neutral-500 hover:text-white' : 'border-neutral-200 hover:bg-neutral-100 text-neutral-500 hover:text-black'}`}
										>
											<Edit className="w-3 h-3" />
										</button>
										<button
											onClick={() => handleDeleteEmail(mail.pm_id)}
											className={`p-1.5 border transition-colors ${isDark ? 'border-[#333] hover:bg-red-500/10 text-red-500 hover:text-red-400' : 'border-neutral-200 hover:bg-red-50 text-red-500 hover:text-red-600'}`}
										>
											<Trash2 className="w-3 h-3" />
										</button>
									</div>
								</div>
							))}
						</div>
					) : (
						<p className={`font-mono text-[10px] ${isDark ? 'text-neutral-600' : 'text-neutral-400'}`}>[ NO_EMAIL_RECORDS_FOUND ]</p>
					)}
				</div>
			</div>

			{/* Locations Module */}
			<div id="section-locations" className={`mt-4 border p-4 ${isDark ? 'bg-[#111] border-[#222]' : 'bg-white border-neutral-200'}`}>
				<div className={`flex justify-between items-center mb-4 pb-2 border-b ${isDark ? 'border-[#222]' : 'border-neutral-200'}`}>
					<h2 className={`text-[10px] font-mono tracking-widest uppercase ${isDark ? 'text-emerald-500' : 'text-emerald-700'}`}>
						// INTEL: LOCATIONS
					</h2>
					<div className="flex items-center space-x-2">
						<div className={`flex border ${isDark ? 'border-[#333] bg-[#0a0a0a]' : 'border-neutral-300 bg-neutral-50'} p-0.5`}>
							<button
								onClick={() => setLocationViewType('table')}
								className={`p-1.5 transition-colors ${locationViewType === 'table' ? (isDark ? 'bg-[#222] text-white' : 'bg-white shadow text-black') : (isDark ? 'text-neutral-500 hover:text-white' : 'text-neutral-500 hover:text-black')}`}
								title="Table View"
							>
								<Table className="w-3.5 h-3.5" />
							</button>
							<button
								onClick={() => setLocationViewType('map')}
								className={`p-1.5 transition-colors ${locationViewType === 'map' ? (isDark ? 'bg-[#222] text-white' : 'bg-white shadow text-black') : (isDark ? 'text-neutral-500 hover:text-white' : 'text-neutral-500 hover:text-black')}`}
								title="Map View"
							>
								<Map className="w-3.5 h-3.5" />
							</button>
						</div>
						<button
							onClick={() => {
								setEditingItem(null);
								setShowLocationModal(true);
							}}
							className={`flex items-center space-x-1.5 px-3 py-1.5 text-[10px] font-mono tracking-wider uppercase border text-emerald-500 hover:bg-emerald-500/10 transition-colors ${isDark ? 'border-[#333] bg-[#0a0a0a]' : 'border-neutral-300 bg-neutral-50'}`}
						>
							<Plus className="w-3 h-3" />
							<span>ADD LOC</span>
						</button>
					</div>
				</div>

				{locationViewType === 'table' ? (
					<div className="space-y-2">
						{person.locations && person.locations.length > 0 ? (
							<div className={`border overflow-hidden ${isDark ? 'border-[#222]' : 'border-neutral-200'}`}>
								<table className="w-full text-[10px] font-mono">
									<thead className={isDark ? 'bg-[#0a0a0a] border-b border-[#222]' : 'bg-neutral-50 border-b border-neutral-200'}>
										<tr>
											<th className={`px-4 py-2 text-left font-normal ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>TITLE</th>
											<th className={`px-4 py-2 text-left font-normal ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>LOCATION</th>
											<th className={`px-4 py-2 text-left font-normal ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>STATUS</th>
											<th className={`px-4 py-2 text-right font-normal ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>ACT</th>
										</tr>
									</thead>
									<tbody className={`divide-y ${isDark ? 'divide-[#222]' : 'divide-neutral-200'}`}>
										{person.locations.map((location) => (
											<tr key={location.pl_id} className={`group ${isDark ? 'hover:bg-[#1a1a1a] bg-[#111]' : 'hover:bg-neutral-50 bg-white'}`}>
												<td className={`px-4 py-2 uppercase ${isDark ? 'text-neutral-200' : 'text-neutral-800'}`}>
													{location.pl_locationTitle || '-'}
												</td>
												<td className={`px-4 py-2 truncate max-w-xs ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>
													{location.pl_location}
												</td>
												<td className={`px-4 py-2 uppercase ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>
													{STATUS_MAP[location.pl_status] || location.pl_status}
												</td>
												<td className="px-4 py-2 text-right">
													<div className="flex items-center justify-end space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
														<button
															onClick={() => {
																setEditingItem(location);
																setShowLocationModal(true);
															}}
															className={`p-1 rounded ${isDark ? 'hover:bg-[#333] text-neutral-400' : 'hover:bg-neutral-200 text-neutral-600'}`}
														>
															<Edit className="w-3 h-3" />
														</button>
														<button
															onClick={() => handleDeleteLocation(location.pl_id)}
															className={`p-1 rounded ${isDark ? 'hover:bg-[#333] text-red-500' : 'hover:bg-neutral-200 text-red-600'}`}
														>
															<Trash2 className="w-3 h-3" />
														</button>
													</div>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						) : (
							<p className={`font-mono text-[10px] ${isDark ? 'text-neutral-600' : 'text-neutral-400'}`}>[ NO_LOCATION_RECORDS_FOUND ]</p>
						)}
					</div>
				) : (
					<div className="space-y-4">
						{person.locations && person.locations.length > 0 ? (
							person.locations.map((location) => (
								<div key={location.pl_id} className={`border overflow-hidden flex flex-col ${isDark ? 'bg-[#0a0a0a] border-[#222]' : 'bg-neutral-50 border-neutral-200'}`}>
									<div className="p-3 flex items-start justify-between border-b border-inherit">
										<div>
											{location.pl_locationTitle && (
												<h4 className={`font-mono text-xs font-bold tracking-widest uppercase mb-1 ${isDark ? 'text-neutral-200' : 'text-neutral-800'}`}>{location.pl_locationTitle}</h4>
											)}
											<p className={`text-[10px] font-mono leading-relaxed max-w-2xl ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>{location.pl_location}</p>
											<p className={`text-[10px] font-mono mt-2 uppercase ${isDark ? 'text-emerald-500' : 'text-emerald-600'}`}>STATUS: {STATUS_MAP[location.pl_status] || location.pl_status}</p>
										</div>
										<div className="flex items-center space-x-2 shrink-0">
											<button
												onClick={() => {
													setEditingItem(location);
													setShowLocationModal(true);
												}}
												className={`p-1.5 border transition-colors ${isDark ? 'border-[#333] hover:bg-[#1a1a1a] text-neutral-500 hover:text-white' : 'border-neutral-200 hover:bg-neutral-100 text-neutral-500 hover:text-black'}`}
											>
												<Edit className="w-3 h-3" />
											</button>
											<button
												onClick={() => handleDeleteLocation(location.pl_id)}
												className={`p-1.5 border transition-colors ${isDark ? 'border-[#333] hover:bg-red-500/10 text-red-500 hover:text-red-400' : 'border-neutral-200 hover:bg-red-50 text-red-500 hover:text-red-600'}`}
											>
												<Trash2 className="w-3 h-3" />
											</button>
										</div>
									</div>
									<div className="h-48 md:h-64 relative bg-neutral-200 dark:bg-neutral-800">
										<iframe
											width="100%"
											height="100%"
											frameBorder="0"
											scrolling="no"
											marginHeight={0}
											marginWidth={0}
											className="absolute inset-0 contrast-125 opacity-80"
											src={`https://maps.google.com/maps?width=100%25&height=100%25&hl=en&q=${encodeURIComponent(location.pl_location)}&t=&z=14&ie=UTF8&iwloc=B&output=embed`}
											title={location.pl_location}
										/>
									</div>
								</div>
							))
						) : (
							<p className={`font-mono text-[10px] ${isDark ? 'text-neutral-600' : 'text-neutral-400'}`}>[ NO_LOCATION_RECORDS_FOUND ]</p>
						)}
					</div>
				)}
			</div>

			{/* Socials Module */}
			<div id="section-socials" className={`mt-4 border p-4 ${isDark ? 'bg-[#111] border-[#222]' : 'bg-white border-neutral-200'}`}>
				<div className={`flex justify-between items-center mb-4 pb-2 border-b ${isDark ? 'border-[#222]' : 'border-neutral-200'}`}>
					<h2 className={`text-[10px] font-mono tracking-widest uppercase ${isDark ? 'text-emerald-500' : 'text-emerald-700'}`}>
						// NETWORK: SOCIALS
					</h2>
					<button
						onClick={() => {
							setEditingItem(null);
							setShowSocialModal(true);
						}}
						className={`flex items-center space-x-1.5 px-3 py-1.5 text-[10px] font-mono tracking-wider uppercase border text-emerald-500 hover:bg-emerald-500/10 transition-colors ${isDark ? 'border-[#333] bg-[#0a0a0a]' : 'border-neutral-300 bg-neutral-50'}`}
					>
						<Plus className="w-3 h-3" />
						<span>ADD SOCIAL</span>
					</button>
				</div>
				<div className="space-y-6">
					{person.instaUsers && person.instaUsers.length > 0 && (
						<div>
							<h3 className={`text-[10px] font-mono tracking-widest mb-3 uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>// PLATFORM: INSTAGRAM</h3>
							<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
								{person.instaUsers.map((insta) => (
									<div
										key={insta.iu_id}
										onClick={() => navigate(`/stalker/socials/instagram/${insta?.iu_id}`)}
										className={`p-3 border cursor-pointer relative group transition-all ${isDark ? 'bg-[#0a0a0a] border-[#222] hover:border-[#444]' : 'bg-neutral-50 border-neutral-200 hover:border-neutral-300'}`}
									>
										<div className="flex items-start space-x-3 mb-3">
											{insta.iu_profilePicUrl ? (
												<img 
													src={getProxyUrl(insta.iu_profilePicUrl) || ''} 
													alt={insta.iu_username}
													className={`w-10 h-10 object-cover border ${isDark ? 'border-[#333]' : 'border-neutral-300'}`}
												/>
											) : (
												<div className={`w-10 h-10 flex items-center justify-center border ${isDark ? 'bg-[#111] border-[#333]' : 'bg-neutral-200 border-neutral-300'}`}>
													<Instagram className="w-4 h-4 text-neutral-500" />
												</div>
											)}
											<div className="flex-1 min-w-0">
												<div className="flex items-center space-x-1">
													<p className={`font-mono text-xs font-bold truncate uppercase ${isDark ? 'text-neutral-200' : 'text-neutral-800'}`}>
														@{insta.iu_username}
													</p>
													{insta.iu_isVerified === '1' && (
														<CheckCircle className="w-3 h-3 text-blue-500 flex-shrink-0" strokeWidth={2.5} />
													)}
													{insta.iu_isPrivate === '1' && (
														<Lock className="w-3 h-3 text-neutral-500 flex-shrink-0" />
													)}
												</div>
												{insta.iu_name && (
													<p className={`text-[10px] font-mono truncate uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>{insta.iu_name}</p>
												)}
											</div>
										</div>
										<div className="grid grid-cols-3 gap-2 mb-1">
											<div className={`text-center p-1 border ${isDark ? 'border-[#222] bg-[#111]' : 'border-neutral-200 bg-white'}`}>
												<p className={`text-[10px] font-mono font-bold ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`}>
													{insta.iu_followerCount?.toLocaleString() || '0'}
												</p>
												<p className={`text-[8px] font-mono tracking-widest ${isDark ? 'text-neutral-600' : 'text-neutral-400'}`}>FLW</p>
											</div>
											<div className={`text-center p-1 border ${isDark ? 'border-[#222] bg-[#111]' : 'border-neutral-200 bg-white'}`}>
												<p className={`text-[10px] font-mono font-bold ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`}>
													{insta.iu_followingCount?.toLocaleString() || '0'}
												</p>
												<p className={`text-[8px] font-mono tracking-widest ${isDark ? 'text-neutral-600' : 'text-neutral-400'}`}>FLG</p>
											</div>
											<div className={`text-center p-1 border ${isDark ? 'border-[#222] bg-[#111]' : 'border-neutral-200 bg-white'}`}>
												<p className={`text-[10px] font-mono font-bold ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`}>
													{insta.iu_mediaCount?.toLocaleString() || '0'}
												</p>
												<p className={`text-[8px] font-mono tracking-widest ${isDark ? 'text-neutral-600' : 'text-neutral-400'}`}>MED</p>
											</div>
										</div>
										<div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
											<a
												href={`https://instagram.com/${insta.iu_username}`}
												target="_blank"
												rel="noopener noreferrer"
												onClick={(e) => e.stopPropagation()}
												className={`flex items-center justify-center p-1.5 border transition-colors ${isDark ? 'bg-[#111] border-[#333] hover:bg-[#222] text-neutral-400 hover:text-white' : 'bg-white border-neutral-300 hover:bg-neutral-100 text-neutral-600 hover:text-black'}`}
												title="Open Instagram"
											>
												<ExternalLink className="w-3 h-3" />
											</a>
										</div>
									</div>
								))}
							</div>
						</div>
					)}
					{person.vscoUsers && person.vscoUsers.length > 0 && (
						<div>
							<h3 className={`text-[10px] font-mono tracking-widest mb-3 uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>// PLATFORM: VSCO</h3>
							<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
								{person.vscoUsers.map((vsco) => (
									<div
										key={vsco.vu_id}
										onClick={() => navigate(`/stalker/socials/vsco/${vsco?.vu_id}`)}
										className={`p-3 border cursor-pointer relative group transition-all ${isDark ? 'bg-[#0a0a0a] border-[#222] hover:border-[#444]' : 'bg-neutral-50 border-neutral-200 hover:border-neutral-300'}`}
									>
										<div className="flex items-start space-x-3 mb-1">
											{vsco.vu_avatarUrl && !avatarErrors.has(vsco.vu_id) ? (
												<img 
													src={(() => {
														const url = getProxyUrl(vsco.vu_avatarUrl) || vsco.vu_avatarUrl || '';
														return url && !url.startsWith('http') ? `https://${url}` : url;
													})()} 
													alt={vsco.vu_username}
													className={`w-10 h-10 object-cover border ${isDark ? 'border-[#333]' : 'border-neutral-300'}`}
													onError={() => {
														setAvatarErrors(prev => new Set(prev).add(vsco.vu_id));
													}}
												/>
											) : (
												<div className={`w-10 h-10 flex items-center justify-center border ${isDark ? 'bg-[#111] border-[#333]' : 'bg-neutral-200 border-neutral-300'}`}>
													<ImageLucide className="w-4 h-4 text-neutral-500" />
												</div>
											)}
											<div className="flex-1 min-w-0">
												<p className={`font-mono text-xs font-bold truncate uppercase ${isDark ? 'text-neutral-200' : 'text-neutral-800'}`}>
													@{vsco.vu_username}
												</p>
												{vsco.vu_name && (
													<p className={`text-[10px] font-mono truncate uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>{vsco.vu_name}</p>
												)}
											</div>
										</div>
										<div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
											<a
												href={`https://vsco.co/${vsco.vu_username}`}
												target="_blank"
												rel="noopener noreferrer"
												onClick={(e) => e.stopPropagation()}
												className={`flex items-center justify-center p-1.5 border transition-colors ${isDark ? 'bg-[#111] border-[#333] hover:bg-[#222] text-neutral-400 hover:text-white' : 'bg-white border-neutral-300 hover:bg-neutral-100 text-neutral-600 hover:text-black'}`}
												title="Open VSCO"
											>
												<ExternalLink className="w-3 h-3" />
											</a>
										</div>
									</div>
								))}
							</div>
						</div>
					)}
					{person.tiktokUsers && person.tiktokUsers.length > 0 && (
						<div>
							<h3 className={`text-[10px] font-mono tracking-widest mb-3 uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>// PLATFORM: TIKTOK</h3>
							<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
								{person.tiktokUsers.map((tiktok) => (
									<div
										key={tiktok.tu_id}
										onClick={() => navigate(`/stalker/socials/tiktok/${tiktok?.tu_id}`)}
										className={`p-3 border cursor-pointer relative group transition-all ${isDark ? 'bg-[#0a0a0a] border-[#222] hover:border-[#444]' : 'bg-neutral-50 border-neutral-200 hover:border-neutral-300'}`}
									>
										<div className="flex items-start space-x-3 mb-3">
											{tiktok.tu_avatarUrl && !avatarErrors.has(tiktok.tu_id) ? (
												<img 
													src={getProxyUrl(tiktok.tu_avatarUrl) || ''} 
													alt={tiktok.tu_username}
													className={`w-10 h-10 object-cover border ${isDark ? 'border-[#333]' : 'border-neutral-300'}`}
													onError={() => {
														setAvatarErrors(prev => new Set(prev).add(tiktok.tu_id));
													}}
												/>
											) : (
												<div className={`w-10 h-10 flex items-center justify-center border ${isDark ? 'bg-[#111] border-[#333]' : 'bg-neutral-200 border-neutral-300'}`}>
													<Music className="w-4 h-4 text-neutral-500" />
												</div>
											)}
											<div className="flex-1 min-w-0">
												<div className="flex items-center space-x-1">
													<p className={`font-mono text-xs font-bold truncate uppercase ${isDark ? 'text-neutral-200' : 'text-neutral-800'}`}>
														@{tiktok.tu_username}
													</p>
													{(tiktok.tu_verified === '1' || tiktok.tu_verified === true) && (
														<CheckCircle className="w-3 h-3 text-blue-500 flex-shrink-0" strokeWidth={2.5} />
													)}
													{tiktok.tu_user_isPrivate === '1' && (
														<Lock className="w-3 h-3 text-neutral-500 flex-shrink-0" />
													)}
												</div>
												{tiktok.tu_name && (
													<p className={`text-[10px] font-mono truncate uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>{tiktok.tu_name}</p>
												)}
											</div>
										</div>
										<div className="grid grid-cols-3 gap-2 mb-1">
											<div className={`text-center p-1 border ${isDark ? 'border-[#222] bg-[#111]' : 'border-neutral-200 bg-white'}`}>
												<p className={`text-[10px] font-mono font-bold ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`}>
													{tiktok.tu_followerCount?.toLocaleString() || '0'}
												</p>
												<p className={`text-[8px] font-mono tracking-widest ${isDark ? 'text-neutral-600' : 'text-neutral-400'}`}>FLW</p>
											</div>
											<div className={`text-center p-1 border ${isDark ? 'border-[#222] bg-[#111]' : 'border-neutral-200 bg-white'}`}>
												<p className={`text-[10px] font-mono font-bold ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`}>
													{tiktok.tu_followingCount?.toLocaleString() || '0'}
												</p>
												<p className={`text-[8px] font-mono tracking-widest ${isDark ? 'text-neutral-600' : 'text-neutral-400'}`}>FLG</p>
											</div>
											<div className={`text-center p-1 border ${isDark ? 'border-[#222] bg-[#111]' : 'border-neutral-200 bg-white'}`}>
												<p className={`text-[10px] font-mono font-bold ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`}>
													{tiktok.tu_mediaCount?.toLocaleString() || '0'}
												</p>
												<p className={`text-[8px] font-mono tracking-widest ${isDark ? 'text-neutral-600' : 'text-neutral-400'}`}>MED</p>
											</div>
										</div>
										<div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
											<a
												href={`https://tiktok.com/@${tiktok.tu_username}`}
												target="_blank"
												rel="noopener noreferrer"
												onClick={(e) => e.stopPropagation()}
												className={`flex items-center justify-center p-1.5 border transition-colors ${isDark ? 'bg-[#111] border-[#333] hover:bg-[#222] text-neutral-400 hover:text-white' : 'bg-white border-neutral-300 hover:bg-neutral-100 text-neutral-600 hover:text-black'}`}
												title="Open TikTok"
											>
												<ExternalLink className="w-3 h-3" />
											</a>
										</div>
									</div>
								))}
							</div>
						</div>
					)}
					{person.spotifyUsers && person.spotifyUsers.length > 0 && (
						<div>
							<h3 className={`text-[10px] font-mono tracking-widest mb-3 uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>// PLATFORM: SPOTIFY</h3>
							<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
								{person.spotifyUsers.map((spotify) => (
									<div
										key={spotify.su_id}
										onClick={() => navigate(`/stalker/socials/spotify/${spotify?.su_id}`)}
										className={`p-3 border cursor-pointer ${isDark ? 'bg-[#0a0a0a] border-[#222] hover:border-[#444]' : 'bg-neutral-50 border-neutral-200 hover:border-neutral-300'}`}
									>
										<p className={`font-mono text-xs font-bold uppercase ${isDark ? 'text-neutral-200' : 'text-neutral-800'}`}>{spotify.su_username}</p>
										{spotify.su_displayName && (
											<p className={`text-[10px] font-mono truncate uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>{spotify.su_displayName}</p>
										)}
									</div>
								))}
							</div>
						</div>
					)}
					{(!person.instaUsers?.length && !person.vscoUsers?.length && !person.tiktokUsers?.length && !person.spotifyUsers?.length) && (
						<p className={`font-mono text-[10px] ${isDark ? 'text-neutral-600' : 'text-neutral-400'}`}>[ NO_SOCIAL_RECORDS_FOUND ]</p>
					)}
				</div>
			</div>

			{/* Medias Module */}
			<div id="section-medias" className={`mt-4 border p-4 ${isDark ? 'bg-[#111] border-[#222]' : 'bg-white border-neutral-200'}`}>
				<div className={`flex justify-between items-center mb-4 pb-2 border-b ${isDark ? 'border-[#222]' : 'border-neutral-200'}`}>
					<h2 className={`text-[10px] font-mono tracking-widest uppercase ${isDark ? 'text-emerald-500' : 'text-emerald-700'}`}>
						// ASSETS: MEDIA ({mediaTotalCount || 0})
					</h2>
					<button
						onClick={() => {
							setEditingItem(null);
							setShowMediaModal(true);
						}}
						className={`flex items-center space-x-1.5 px-3 py-1.5 text-[10px] font-mono tracking-wider uppercase border text-emerald-500 hover:bg-emerald-500/10 transition-colors ${isDark ? 'border-[#333] bg-[#0a0a0a]' : 'border-neutral-300 bg-neutral-50'}`}
					>
						<Plus className="w-3 h-3" />
						<span>ADD MEDIA</span>
					</button>
				</div>
				<div className="space-y-2">
					{medias && medias.length > 0 ? (
						<>
							<div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-3">
								{medias.map((media) => {
								const mediaUrl = media.sm_filePath 
									? getStaticFileUrl(media.sm_filePath) 
									: (media.sm_originalUrl ? getProxyUrl(media.sm_originalUrl) : null);
								return (
								<div key={media.sm_id} className={`p-2 border relative group ${isDark ? 'bg-[#0a0a0a] border-[#222]' : 'bg-neutral-50 border-neutral-200'}`}>
									{mediaUrl && (
										<div className="mb-2 relative">
											{media.sm_mediaType === 'video' ? (
												<div className={`relative w-full h-32 md:h-40 border overflow-hidden cursor-pointer hover:opacity-80 transition-opacity ${isDark ? 'border-[#333]' : 'border-neutral-300'}`} onClick={(e) => {
													e.stopPropagation();
													setLightboxImage(mediaUrl);
													setLightboxType('video');
													setIsAvatarLightbox(false);
													setLightboxMedia(media);
													setZoomLevel(1);
													setImagePosition({ x: 0, y: 0 });
												}}>
													<video 
														src={mediaUrl} 
														className="w-full h-full object-cover" 
														preload="metadata"
													/>
													<div className="absolute inset-0 flex items-center justify-center bg-black/40">
														<Play className="w-8 h-8 text-neutral-300" fill="currentColor" />
													</div>
												</div>
											) : (
												<div className={`w-full h-32 md:h-40 border overflow-hidden ${isDark ? 'border-[#333]' : 'border-neutral-300'}`}>
													<img 
														src={mediaUrl} 
														alt={media.sm_fileName || 'Media'} 
														className="w-full h-full object-cover cursor-pointer transition-opacity duration-300"
														onClick={() => {
															setLightboxImage(mediaUrl);
															setLightboxType('image');
															setIsAvatarLightbox(false);
															setLightboxMedia(media);
															setZoomLevel(1);
															setImagePosition({ x: 0, y: 0 });
														}}
													/>
												</div>
											)}
											{/* Top Right Buttons */}
											<div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-start items-end p-2 gap-2 pointer-events-none">
												<div className="pointer-events-auto flex items-center space-x-1">
													<button
														onClick={async (e) => {
															e.stopPropagation();
															try {
																const response = await fetch(mediaUrl);
																const blob = await response.blob();
																const url = window.URL.createObjectURL(blob);
																const link = document.createElement('a');
																link.href = url;
																link.download = media.sm_fileName || 'media';
																document.body.appendChild(link);
																link.click();
																document.body.removeChild(link);
																window.URL.revokeObjectURL(url);
															} catch (error) {
																console.error('Download error:', error);
															}
														}}
														className={`p-1.5 border transition-colors ${isDark ? 'bg-black/80 border-[#333] hover:bg-[#222] text-neutral-400 hover:text-white' : 'bg-white/90 border-neutral-300 hover:bg-neutral-100 text-neutral-600 hover:text-black'}`}
														title="Download"
													>
														<Download className="w-3.5 h-3.5" />
													</button>
													<button
														onClick={(e) => {
															e.stopPropagation();
															setEditingItem(media);
															setShowMediaModal(true);
														}}
														className={`p-1.5 border transition-colors ${isDark ? 'bg-black/80 border-[#333] hover:bg-[#222] text-neutral-400 hover:text-white' : 'bg-white/90 border-neutral-300 hover:bg-neutral-100 text-neutral-600 hover:text-black'}`}
														title="Edit"
													>
														<Edit className="w-3.5 h-3.5" />
													</button>
													<button
														onClick={(e) => {
															e.stopPropagation();
															handleDeleteMedia(media.sm_id);
														}}
														className={`p-1.5 border transition-colors ${isDark ? 'bg-black/80 border-[#333] hover:bg-red-900/50 text-red-500 hover:text-red-400' : 'bg-white/90 border-neutral-300 hover:bg-red-50 text-red-500 hover:text-red-600'}`}
														title="Delete"
													>
														<Trash2 className="w-3.5 h-3.5" />
													</button>
												</div>
											</div>
										</div>
									)}
									<div className="mt-2 space-y-1">
										<p className={`font-mono text-[10px] tracking-widest font-bold uppercase ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`}>
											{PLATFORM_MAP[media.sm_platform] || media.sm_platform}
										</p>
										{media.sm_downloadedAt && (
											<p className={`font-mono text-[8px] uppercase tracking-wider ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
												{formatDate(media.sm_downloadedAt)}
											</p>
										)}
										{media.sm_description && (
											<p 
												className={`text-[10px] line-clamp-2 mt-1 ${isDark ? 'text-neutral-400' : 'text-neutral-500'}`}
												title={media.sm_description}
											>
												{media.sm_description}
											</p>
										)}
									</div>
								</div>
								);
							})}
							</div>
							{/* Infinite scroll observer target */}
							{mediaHasMore && (
								<div ref={mediaObserverTarget} className="flex justify-center py-4 min-h-[50px]">
									{mediaLoading && (
										<p className={`font-mono text-[10px] animate-pulse ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>[ RETRIEVING_ADDITIONAL_ASSETS... ]</p>
									)}
								</div>
							)}
						</>
					) : (
						<p className={`font-mono text-[10px] ${isDark ? 'text-neutral-600' : 'text-neutral-400'}`}>[ NO_MEDIA_ASSETS_FOUND ]</p>
					)}
				</div>
			</div>

			{/* Logs Module */}
			<div id="section-logs" className={`mt-4 border p-4 ${isDark ? 'bg-[#111] border-[#222]' : 'bg-white border-neutral-200'}`}>
				<div className={`flex justify-between items-center mb-4 pb-2 border-b ${isDark ? 'border-[#222]' : 'border-neutral-200'}`}>
					<h2 className={`text-[10px] font-mono tracking-widest uppercase ${isDark ? 'text-emerald-500' : 'text-emerald-700'}`}>
						// SYSTEM: AUDIT_LOGS
					</h2>
				</div>
				<div className="space-y-2">
					{stalkerLogs && stalkerLogs.length > 0 ? (
						<div className={`border overflow-hidden ${isDark ? 'border-[#222]' : 'border-neutral-200'}`}>
							<div className="max-h-96 overflow-y-auto">
								<table className="w-full text-[10px] font-mono whitespace-nowrap">
									<thead className={`sticky top-0 z-10 ${isDark ? 'bg-[#0a0a0a] border-b border-[#222]' : 'bg-neutral-50 border-b border-neutral-200'}`}>
										<tr>
											<th className={`px-4 py-2 text-left font-normal ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>PLATFORM</th>
											<th className={`px-4 py-2 text-left font-normal ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>EVENT</th>
											<th className={`px-4 py-2 text-left font-normal ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>DELTA</th>
											<th className={`px-4 py-2 text-left font-normal ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>TIMESTAMP</th>
										</tr>
									</thead>
									<tbody className={`divide-y ${isDark ? 'divide-[#222]' : 'divide-neutral-200'}`}>
										{stalkerLogs.map((log, index) => (
											<tr key={log.sl_id || index} className={`hover:bg-opacity-50 ${isDark ? 'hover:bg-[#1a1a1a] bg-[#111]' : 'hover:bg-neutral-50 bg-white'}`}>
												<td className={`px-4 py-2 uppercase ${isDark ? 'text-emerald-500' : 'text-emerald-600'}`}>{log.sl_platform || '-'}</td>
												<td className={`px-4 py-2 ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`}>
													{isInstagramLog(log) && isInstagramSupportedChangeType(log.sl_changeType)
														? instagramTitle(log)
														: isTiktokLog(log) && isTiktokSupportedChangeType(log.sl_changeType)
														? tiktokTitle(log)
														: isVSCOLog(log) && isVSCOSupportedChangeType(log.sl_changeType)
														? vscoTitle(log)
														: log.sl_changeType || '-'}
												</td>
												<td className={`px-4 py-2 truncate max-w-sm ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>
													{isInstagramLog(log) && isInstagramSupportedChangeType(log.sl_changeType)
														? instagramDescription(log)
														: isTiktokLog(log) && isTiktokSupportedChangeType(log.sl_changeType)
														? tiktokDescription(log)
														: isVSCOLog(log) && isVSCOSupportedChangeType(log.sl_changeType)
														? vscoDescription(log)
														: log.sl_description || '-'}
												</td>
												<td className={`px-4 py-2 ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>{log.created_at ? new Date(log.created_at).toLocaleString() : '-'}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</div>
					) : (
						<p className={`font-mono text-[10px] ${isDark ? 'text-neutral-600' : 'text-neutral-400'}`}>[ NO_AUDIT_LOGS_FOUND ]</p>
					)}
				</div>
			</div>

			{/* Number Modal */}
			{showNumberModal && createPortal(
				<div
					className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1005] p-4"
					onClick={() => setShowNumberModal(false)}
				>
					<div
						className={`rounded-none border p-6 w-full max-w-md ${isDark ? 'bg-[#0a0a0a] border-[#222]' : 'bg-white border-neutral-300'}`}
						onClick={(e) => e.stopPropagation()}
					>
						<div className="flex justify-between items-center mb-6 border-b pb-4 border-emerald-500/20">
							<h2 className={`text-xs font-mono tracking-[0.2em] font-bold uppercase ${isDark ? 'text-emerald-500' : 'text-emerald-700'}`}>
								// {editingItem ? 'PATCH: CONTACT_NUMBER' : 'INIT: CONTACT_NUMBER'}
							</h2>
							<button
								onClick={() => {
									setShowNumberModal(false);
									setEditingItem(null);
								}}
								className={`p-1 transition-colors ${isDark ? 'hover:bg-emerald-500/10 text-neutral-500' : 'hover:bg-neutral-100 text-neutral-400'}`}
							>
								<X className="w-4 h-4" />
							</button>
						</div>
						<form onSubmit={handleNumberSubmit} className="space-y-6">
							<div className="space-y-2">
								<label className={`block text-[10px] font-mono tracking-widest uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
									PHN_NUMBER <span className="text-red-500">*</span>
								</label>
								<input
									type="text"
									name="pn_number"
									defaultValue={editingItem?.pn_number || ''}
									className={`w-full px-3 py-2 rounded-none border font-mono text-xs transition-colors ${isDark ? 'bg-[#111] border-[#333] text-white focus:border-emerald-500Outline' : 'bg-neutral-50 border-neutral-200 text-neutral-900 focus:border-emerald-500'}`}
									required
									placeholder="+1234567890"
								/>
							</div>
							<div className="space-y-2">
								<label className={`block text-[10px] font-mono tracking-widest uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>STATUS_FLAG</label>
								<select
									name="pn_status"
									defaultValue={editingItem?.pn_status || 'active'}
									className={`w-full px-3 py-2 rounded-none border font-mono text-xs transition-colors ${isDark ? 'bg-[#111] border-[#333] text-white focus:border-emerald-500' : 'bg-neutral-50 border-neutral-200 text-neutral-900 focus:border-emerald-500'}`}
								>
									<option value="active">ACTIVE</option>
									<option value="notactive">INACTIVE</option>
								</select>
							</div>
							<div className="flex justify-end space-x-3 pt-4">
								<button
									type="button"
									onClick={() => {
										setShowNumberModal(false);
										setEditingItem(null);
									}}
									className={`px-4 py-2 border font-mono text-[10px] tracking-widest transition-colors ${isDark ? 'border-[#333] text-neutral-500 hover:bg-[#222]' : 'bg-neutral-100 text-neutral-600 border-neutral-300 hover:bg-neutral-200'}`}
								>
									ABORT
								</button>
								<button
									type="submit"
									className="px-6 py-2 border border-emerald-500/50 bg-emerald-500/10 text-emerald-500 font-mono text-[10px] tracking-[0.2em] font-bold hover:bg-emerald-500 hover:text-black transition-all"
								>
									{editingItem ? 'EXEC: UPDATE' : 'EXEC: COMMIT'}
								</button>
							</div>
						</form>
					</div>
				</div>,
				document.body
			)}

			{/* Email Modal */}
			{showEmailModal && createPortal(
				<div
					className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1005] p-4"
					onClick={() => setShowEmailModal(false)}
				>
					<div
						className={`rounded-none border p-6 w-full max-w-md ${isDark ? 'bg-[#0a0a0a] border-[#222]' : 'bg-white border-neutral-300'}`}
						onClick={(e) => e.stopPropagation()}
					>
						<div className="flex justify-between items-center mb-6 border-b pb-4 border-emerald-500/20">
							<h2 className={`text-xs font-mono tracking-[0.2em] font-bold uppercase ${isDark ? 'text-emerald-500' : 'text-emerald-700'}`}>
								// {editingItem ? 'PATCH: CONTACT_EMAIL' : 'INIT: CONTACT_EMAIL'}
							</h2>
							<button
								onClick={() => {
									setShowEmailModal(false);
									setEditingItem(null);
								}}
								className={`p-1 transition-colors ${isDark ? 'hover:bg-emerald-500/10 text-neutral-500' : 'hover:bg-neutral-100 text-neutral-400'}`}
							>
								<X className="w-4 h-4" />
							</button>
						</div>
						<form onSubmit={handleEmailSubmit} className="space-y-6">
							<div className="space-y-2">
								<label className={`block text-[10px] font-mono tracking-widest uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
									MAIL_ADDR <span className="text-red-500">*</span>
								</label>
								<input
									type="email"
									name="pm_mail"
									defaultValue={editingItem?.pm_mail || ''}
									className={`w-full px-3 py-2 rounded-none border font-mono text-xs transition-colors ${isDark ? 'bg-[#111] border-[#333] text-white focus:border-emerald-500' : 'bg-neutral-50 border-neutral-200 text-neutral-900 focus:border-emerald-500'}`}
									required
									placeholder="target@example.com"
								/>
							</div>
							<div className="space-y-2">
								<label className={`block text-[10px] font-mono tracking-widest uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>STATUS_FLAG</label>
								<select
									name="pm_status"
									defaultValue={editingItem?.pm_status || 'active'}
									className={`w-full px-3 py-2 rounded-none border font-mono text-xs transition-colors ${isDark ? 'bg-[#111] border-[#333] text-white focus:border-emerald-500' : 'bg-neutral-50 border-neutral-200 text-neutral-900 focus:border-emerald-500'}`}
								>
									<option value="active">ACTIVE</option>
									<option value="notactive">INACTIVE</option>
								</select>
							</div>
							<div className="flex justify-end space-x-3 pt-4">
								<button
									type="button"
									onClick={() => {
										setShowEmailModal(false);
										setEditingItem(null);
									}}
									className={`px-4 py-2 border font-mono text-[10px] tracking-widest transition-colors ${isDark ? 'border-[#333] text-neutral-500 hover:bg-[#222]' : 'bg-neutral-100 text-neutral-600 border-neutral-300 hover:bg-neutral-200'}`}
								>
									ABORT
								</button>
								<button
									type="submit"
									className="px-6 py-2 border border-emerald-500/50 bg-emerald-500/10 text-emerald-500 font-mono text-[10px] tracking-[0.2em] font-bold hover:bg-emerald-500 hover:text-black transition-all"
								>
									{editingItem ? 'EXEC: UPDATE' : 'EXEC: COMMIT'}
								</button>
							</div>
						</form>
					</div>
				</div>,
				document.body
			)}

			{/* Location Modal */}
			{showLocationModal && createPortal(
				<div
					className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1005] p-4"
					onClick={() => setShowLocationModal(false)}
				>
					<div
						className={`rounded-none border p-6 w-full max-w-md ${isDark ? 'bg-[#0a0a0a] border-[#222]' : 'bg-white border-neutral-300'}`}
						onClick={(e) => e.stopPropagation()}
					>
						<div className="flex justify-between items-center mb-6 border-b pb-4 border-emerald-500/20">
							<h2 className={`text-xs font-mono tracking-[0.2em] font-bold uppercase ${isDark ? 'text-emerald-500' : 'text-emerald-700'}`}>
								// {editingItem ? 'PATCH: LOCATION_INTEL' : 'INIT: LOCATION_INTEL'}
							</h2>
							<button
								onClick={() => {
									setShowLocationModal(false);
									setEditingItem(null);
								}}
								className={`p-1 transition-colors ${isDark ? 'hover:bg-emerald-500/10 text-neutral-500' : 'hover:bg-neutral-100 text-neutral-400'}`}
							>
								<X className="w-4 h-4" />
							</button>
						</div>
						<form onSubmit={handleLocationSubmit} className="space-y-6">
							<div className="space-y-2">
								<label className={`block text-[10px] font-mono tracking-widest uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
									LOC_TAG
								</label>
								<input
									type="text"
									name="pl_locationTitle"
									defaultValue={editingItem?.pl_locationTitle || ''}
									className={`w-full px-3 py-2 rounded-none border font-mono text-xs transition-colors ${isDark ? 'bg-[#111] border-[#333] text-white focus:border-emerald-500' : 'bg-neutral-50 border-neutral-200 text-neutral-900 focus:border-emerald-500'}`}
									placeholder="HOME, WORK, ETC."
								/>
							</div>
							<div className="space-y-2">
								<label className={`block text-[10px] font-mono tracking-widest uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
									LOC_COORD_ADDR <span className="text-red-500">*</span>
								</label>
								<input
									type="text"
									name="pl_location"
									defaultValue={editingItem?.pl_location || ''}
									className={`w-full px-3 py-2 rounded-none border font-mono text-xs transition-colors ${isDark ? 'bg-[#111] border-[#333] text-white focus:border-emerald-500' : 'bg-neutral-50 border-neutral-200 text-neutral-900 focus:border-emerald-500'}`}
									required
									placeholder="123 MAIN ST, CITY, COUN."
								/>
							</div>
							<div className="space-y-2">
								<label className={`block text-[10px] font-mono tracking-widest uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>STATUS_FLAG</label>
								<select
									name="pl_status"
									defaultValue={editingItem?.pl_status || 'active'}
									className={`w-full px-3 py-2 rounded-none border font-mono text-xs transition-colors ${isDark ? 'bg-[#111] border-[#333] text-white focus:border-emerald-500' : 'bg-neutral-50 border-neutral-200 text-neutral-900 focus:border-emerald-500'}`}
								>
									<option value="active">ACTIVE</option>
									<option value="notactive">INACTIVE</option>
								</select>
							</div>
							<div className="flex justify-end space-x-3 pt-4">
								<button
									type="button"
									onClick={() => {
										setShowLocationModal(false);
										setEditingItem(null);
									}}
									className={`px-4 py-2 border font-mono text-[10px] tracking-widest transition-colors ${isDark ? 'border-[#333] text-neutral-500 hover:bg-[#222]' : 'bg-neutral-100 text-neutral-600 border-neutral-300 hover:bg-neutral-200'}`}
								>
									ABORT
								</button>
								<button
									type="submit"
									className="px-6 py-2 border border-emerald-500/50 bg-emerald-500/10 text-emerald-500 font-mono text-[10px] tracking-[0.2em] font-bold hover:bg-emerald-500 hover:text-black transition-all"
								>
									{editingItem ? 'EXEC: UPDATE' : 'EXEC: COMMIT'}
								</button>
							</div>
						</form>
					</div>
				</div>,
				document.body
			)}

			{/* Media Modal */}
			{showMediaModal && createPortal(
				<div
					className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1005] p-4"
					onClick={() => setShowMediaModal(false)}
				>
					<div
						className={`rounded-lg p-6 w-full max-w-md ${isDark ? 'bg-gray-900' : 'bg-white'}`}
						onClick={(e) => e.stopPropagation()}
					>
						<div className="flex justify-between items-center mb-4">
							<h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
								{editingItem ? 'Edit Media' : 'Add Media'}
							</h2>
							<button
								onClick={() => {
									setShowMediaModal(false);
									setEditingItem(null);
								}}
								className={`p-1 rounded ${isDark ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
							>
								<X className="w-5 h-5" />
							</button>
						</div>
						<form onSubmit={handleMediaSubmit} className="space-y-4" encType="multipart/form-data">
							<div>
								<label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
									Platform <span className="text-red-500">*</span>
								</label>
								<select
									name="sm_platform"
									defaultValue={editingItem?.sm_platform || ''}
									className={`w-full px-3 py-2 rounded border ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
									required
									disabled={!!editingItem}
								>
									<option value="">Select Platform</option>
									<option value="instagram">Instagram</option>
									<option value="vsco">VSCO</option>
									<option value="tiktok">TikTok</option>
									<option value="x">X (Twitter)</option>
									<option value="snapchat">Snapchat</option>
									<option value="spotify">Spotify</option>
								</select>
							</div>
							<div>
								<label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Media Type</label>
								<select
									name="sm_mediaType"
									defaultValue={editingItem?.sm_mediaType || 'image'}
									className={`w-full px-3 py-2 rounded border ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
								>
									<option value="image">Image</option>
									<option value="video">Video</option>
								</select>
							</div>
							<div>
								<label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
									File <span className="text-red-500">*</span>
								</label>
								<div className="flex items-start space-x-4">
									{/* Preview */}
									{mediaPreview && (
										<div className="relative">
											{mediaFile?.type.startsWith('video/') ? (
												<video
													src={mediaPreview}
													className="w-32 h-32 rounded object-cover border-2 border-gray-300"
													controls
												/>
											) : (
												<img
													src={mediaPreview}
													alt="Media preview"
													className="w-32 h-32 rounded object-cover border-2 border-gray-300"
												/>
											)}
											<button
												type="button"
												onClick={() => {
													setMediaFile(null);
													setMediaPreview(null);
												}}
												className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
											>
												<X className="w-4 h-4" />
											</button>
										</div>
									)}
									{/* Upload Area */}
									<div className="flex-1">
										<label
											className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
												isDraggingMedia
													? isDark
														? 'border-red-500 bg-red-900/20'
														: 'border-red-500 bg-red-50'
													: isDark
													? 'border-gray-700 hover:border-gray-600 bg-gray-800 hover:bg-gray-750'
													: 'border-gray-300 hover:border-gray-400 bg-gray-50 hover:bg-gray-100'
											}`}
											onDragEnter={(e) => {
												e.preventDefault();
												e.stopPropagation();
												setIsDraggingMedia(true);
											}}
											onDragLeave={(e) => {
												e.preventDefault();
												e.stopPropagation();
												setIsDraggingMedia(false);
											}}
											onDragOver={(e) => {
												e.preventDefault();
												e.stopPropagation();
											}}
											onDrop={(e) => {
												e.preventDefault();
												e.stopPropagation();
												setIsDraggingMedia(false);
												const file = e.dataTransfer.files[0];
												if (file && (file.type.startsWith('image/') || file.type.startsWith('video/'))) {
													handleMediaFile(file);
												}
											}}
										>
											<div className="flex flex-col items-center justify-center pt-5 pb-6">
												<svg
													className={`w-10 h-10 mb-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
													fill="none"
													stroke="currentColor"
													viewBox="0 0 24 24"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														strokeWidth={2}
														d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
													/>
												</svg>
												<p className={`mb-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
													<span className="font-semibold">Click to upload</span> or drag and drop
												</p>
												<p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
													Image or Video (MAX. 50MB)
												</p>
											</div>
											<input
												type="file"
												name="sm_file"
												accept="image/*,video/*"
												className="hidden"
												required={!editingItem && !mediaFile}
												onChange={(e) => {
													const file = e.target.files?.[0] || null;
													if (file) {
														handleMediaFile(file);
													}
												}}
											/>
										</label>
										{mediaFile && !mediaPreview && (
											<p className={`text-sm mt-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
												Selected: {mediaFile.name}
											</p>
										)}
									</div>
								</div>
							</div>
							<div>
								<label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Description</label>
								<textarea
									name="sm_description"
									rows={3}
									defaultValue={editingItem?.sm_description || ''}
									className={`w-full px-3 py-2 rounded border ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
									placeholder="Optional description..."
								/>
							</div>
							<div className="flex justify-end space-x-2">
								<button
									type="button"
									onClick={() => {
										setShowMediaModal(false);
										setEditingItem(null);
									}}
									className={`px-4 py-2 rounded ${isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}
								>
									Cancel
								</button>
								<button
									type="submit"
									className="px-4 py-2 rounded text-white hover:opacity-90 transition-opacity"
									style={{ backgroundColor: '#E50914' }}
								>
									{editingItem ? 'Update' : 'Create'}
								</button>
							</div>
						</form>
					</div>
				</div>,
				document.body
			)}

			{/* Social Modal */}
			{showSocialModal && createPortal(
				<div
					className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1005] p-4"
					onClick={() => setShowSocialModal(false)}
				>
					<div
						className={`rounded-none border p-6 w-full max-w-md ${isDark ? 'bg-[#0a0a0a] border-[#222]' : 'bg-white border-neutral-300'}`}
						onClick={(e) => e.stopPropagation()}
					>
						<div className="flex justify-between items-center mb-6 border-b pb-4 border-emerald-500/20">
							<h2 className={`text-xs font-mono tracking-[0.2em] font-bold uppercase ${isDark ? 'text-emerald-500' : 'text-emerald-700'}`}>
								// {editingItem ? 'PATCH: SOCIAL_ACCOUNT' : 'INIT: SOCIAL_ACCOUNT'}
							</h2>
							<button
								onClick={() => {
									setShowSocialModal(false);
									setEditingItem(null);
								}}
								className={`p-1 transition-colors ${isDark ? 'hover:bg-emerald-500/10 text-neutral-500' : 'hover:bg-neutral-100 text-neutral-400'}`}
							>
								<X className="w-4 h-4" />
							</button>
						</div>
						<form onSubmit={handleSocialSubmit} className="space-y-6">
							<div className="space-y-2">
								<label className={`block text-[10px] font-mono tracking-widest uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
									PLATFORM <span className="text-red-500">*</span>
								</label>
								<select
									name="platform"
									defaultValue=""
									className={`w-full px-3 py-2 rounded-none border font-mono text-xs transition-colors ${isDark ? 'bg-[#111] border-[#333] text-white focus:border-emerald-500' : 'bg-neutral-50 border-neutral-200 text-neutral-900 focus:border-emerald-500'}`}
									required
								>
									<option value="">SELECT</option>
									<option value="instagram">INSTAGRAM</option>
									<option value="vsco">VSCO</option>
									<option value="tiktok">TIKTOK</option>
									<option value="x">X / TWITTER</option>
									<option value="snapchat">SNAPCHAT</option>
									<option value="spotify">SPOTIFY</option>
								</select>
							</div>
							<div className="space-y-2">
								<label className={`block text-[10px] font-mono tracking-widest uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
									USERNAME_HANDLE <span className="text-red-500">*</span>
								</label>
								<input
									type="text"
									name="username"
									className={`w-full px-3 py-2 rounded-none border font-mono text-xs transition-colors ${isDark ? 'bg-[#111] border-[#333] text-white focus:border-emerald-500' : 'bg-neutral-50 border-neutral-200 text-neutral-900 focus:border-emerald-500'}`}
									required
									placeholder="@USERNAME"
								/>
							</div>
							<div className="flex justify-end space-x-3 pt-4">
								<button
									type="button"
									onClick={() => {
										setShowSocialModal(false);
										setEditingItem(null);
									}}
									className={`px-4 py-2 border font-mono text-[10px] tracking-widest transition-colors ${isDark ? 'border-[#333] text-neutral-500 hover:bg-[#222]' : 'bg-neutral-100 text-neutral-600 border-neutral-300 hover:bg-neutral-200'}`}
								>
									ABORT
								</button>
								<button
									type="submit"
									className="px-6 py-2 border border-emerald-500/50 bg-emerald-500/10 text-emerald-500 font-mono text-[10px] tracking-[0.2em] font-bold hover:bg-emerald-500 hover:text-black transition-all"
								>
									EXEC: COMMIT
								</button>
							</div>
						</form>
					</div>
				</div>,
				document.body
			)}

			{/* Person Edit Modal */}
			{showPersonEditModal && createPortal(
				<div
					className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1005] p-4"
					onClick={() => setShowPersonEditModal(false)}
				>
					<div
						className={`rounded-none border p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto ${isDark ? 'bg-[#0a0a0a] border-[#222]' : 'bg-white border-neutral-300'}`}
						onClick={(e) => e.stopPropagation()}
					>
						<div className="flex justify-between items-center mb-6 border-b pb-4 border-emerald-500/20">
							<h2 className={`text-xs font-mono tracking-[0.2em] font-bold uppercase ${isDark ? 'text-emerald-500' : 'text-emerald-700'}`}>
								// PATCH: TARGET_DOSSIER
							</h2>
							<button
								onClick={() => setShowPersonEditModal(false)}
								className={`p-1 transition-colors ${isDark ? 'hover:bg-emerald-500/10 text-neutral-500' : 'hover:bg-neutral-100 text-neutral-400'}`}
							>
								<X className="w-4 h-4" />
							</button>
						</div>
						<form onSubmit={handlePersonEditSubmit} className="space-y-6" encType="multipart/form-data">
							<div>
								<label className={`block text-[10px] font-mono tracking-widest uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
									PIN_OVERRIDE
								</label>
								<input
									type="text"
									name="person_pin"
									defaultValue={person?.person_pin || ''}
									className={`w-full px-3 py-2 rounded-none border font-mono text-xs transition-colors ${isDark ? 'bg-[#111] border-[#333] text-white focus:border-emerald-500' : 'bg-neutral-50 border-neutral-200 text-neutral-900 focus:border-emerald-500'}`}
									placeholder="####"
								/>
							</div>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
								<div className="space-y-2">
									<label className={`block text-[10px] font-mono tracking-widest uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
										FIRST_NAME <span className="text-red-500">*</span>
									</label>
									<input
										type="text"
										name="person_name"
										defaultValue={person?.person_name || ''}
										className={`w-full px-3 py-2 rounded-none border font-mono text-xs transition-colors ${isDark ? 'bg-[#111] border-[#333] text-white focus:border-emerald-500' : 'bg-neutral-50 border-neutral-200 text-neutral-900 focus:border-emerald-500'}`}
										required
									/>
								</div>
								<div className="space-y-2">
									<label className={`block text-[10px] font-mono tracking-widest uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
										LAST_NAME <span className="text-red-500">*</span>
									</label>
									<input
										type="text"
										name="person_surname"
										defaultValue={person?.person_surname || ''}
										className={`w-full px-3 py-2 rounded-none border font-mono text-xs transition-colors ${isDark ? 'bg-[#111] border-[#333] text-white focus:border-emerald-500' : 'bg-neutral-50 border-neutral-200 text-neutral-900 focus:border-emerald-500'}`}
										required
									/>
								</div>
							</div>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
								<div className="space-y-2">
									<label className={`block text-[10px] font-mono tracking-widest uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
										GENDER_CLASS
									</label>
									<select
										name="person_gender"
										defaultValue={person?.person_gender || 'female'}
										className={`w-full px-3 py-2 rounded-none border font-mono text-xs transition-colors ${isDark ? 'bg-[#111] border-[#333] text-white focus:border-emerald-500' : 'bg-neutral-50 border-neutral-200 text-neutral-900 focus:border-emerald-500'}`}
									>
										<option value="female">FEMALE</option>
										<option value="male">MALE</option>
										<option value="other">OTHER</option>
									</select>
								</div>
								<div className="space-y-2">
									<label className={`block text-[10px] font-mono tracking-widest uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>ORIENTATION</label>
									<select
										name="person_sexualOrientation"
										defaultValue={person?.person_sexualOrientation || 'heterosexual'}
										className={`w-full px-3 py-2 rounded-none border font-mono text-xs transition-colors ${isDark ? 'bg-[#111] border-[#333] text-white focus:border-emerald-500' : 'bg-neutral-50 border-neutral-200 text-neutral-900 focus:border-emerald-500'}`}
									>
										<option value="heterosexual">HETEROSEXUAL</option>
										<option value="homosexual">HOMOSEXUAL</option>
										<option value="bisexual">BISEXUAL</option>
										<option value="other">OTHER</option>
									</select>
								</div>
							</div>
							<div className="space-y-2">
								<label className={`block text-[10px] font-mono tracking-widest uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>BIRTH_DATE</label>
								<input
									type="date"
									name="person_birthdate"
									defaultValue={person?.person_birthdate ? person.person_birthdate.split('T')[0] : ''}
									className={`w-full px-3 py-2 rounded-none border font-mono text-xs transition-colors ${isDark ? 'bg-[#111] border-[#333] text-white focus:border-emerald-500' : 'bg-neutral-50 border-neutral-200 text-neutral-900 focus:border-emerald-500'}`}
								/>
							</div>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
								<div className="space-y-2">
									<label className={`block text-[10px] font-mono tracking-widest uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>BIRTH_CITY</label>
									<input
										type="text"
										name="person_birthCity"
										defaultValue={person?.person_birthCity || ''}
										className={`w-full px-3 py-2 rounded-none border font-mono text-xs transition-colors ${isDark ? 'bg-[#111] border-[#333] text-white focus:border-emerald-500' : 'bg-neutral-50 border-neutral-200 text-neutral-900 focus:border-emerald-500'}`}
									/>
								</div>
								<div className="space-y-2">
									<label className={`block text-[10px] font-mono tracking-widest uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>BIRTH_COUNTRY</label>
									<input
										type="text"
										name="person_birthCountry"
										defaultValue={person?.person_birthCountry || ''}
										className={`w-full px-3 py-2 rounded-none border font-mono text-xs transition-colors ${isDark ? 'bg-[#111] border-[#333] text-white focus:border-emerald-500' : 'bg-neutral-50 border-neutral-200 text-neutral-900 focus:border-emerald-500'}`}
									/>
								</div>
							</div>
							<div className="space-y-2">
								<label className={`block text-[10px] font-mono tracking-widest uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>AVATAR_UPL</label>
								<div className="flex items-start space-x-6">
									{(avatarPreview || person?.person_avatar) && (
										<div className="relative border border-emerald-500/30 p-1 bg-black">
											<img
												src={avatarPreview || getStaticFileUrl(person?.person_avatar) || ''}
												alt="Avatar preview"
												className="w-24 h-24 object-cover"
											/>
											<button
												type="button"
												onClick={() => {
													setAvatarFile(null);
													setAvatarPreview(null);
												}}
												className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white flex items-center justify-center hover:bg-red-600 transition-colors"
											>
												<X className="w-3 h-3" />
											</button>
										</div>
									)}
									<div className="flex-1">
										<label
											className={`flex flex-col items-center justify-center w-full h-24 border border-dashed transition-colors cursor-pointer ${
												isDraggingAvatar
													? 'border-emerald-500 bg-emerald-500/10'
													: isDark ? 'border-[#333] hover:border-emerald-500/50 bg-[#111]' : 'border-neutral-300 hover:border-emerald-500/50 bg-neutral-50'
											}`}
											onDragEnter={(e) => { e.preventDefault(); setIsDraggingAvatar(true); }}
											onDragLeave={(e) => { e.preventDefault(); setIsDraggingAvatar(false); }}
											onDragOver={(e) => e.preventDefault()}
											onDrop={(e) => {
												e.preventDefault();
												setIsDraggingAvatar(false);
												const file = e.dataTransfer.files[0];
												if (file && file.type.startsWith('image/')) handleAvatarFile(file);
											}}
										>
											<p className="text-[10px] font-mono tracking-wide text-neutral-500 uppercase">
												{avatarFile ? avatarFile.name : 'CLICK_OR_DROP_IMG'}
											</p>
											<input
												type="file"
												name="person_avatar"
												accept="image/*"
												className="hidden"
												onChange={(e) => {
													const file = e.target.files?.[0] || null;
													if (file) handleAvatarFile(file);
												}}
											/>
										</label>
									</div>
								</div>
							</div>
							<div className="flex items-center justify-between border-t border-[#222] pt-4">
								<label className={`block text-[10px] font-mono tracking-widest uppercase ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>STEALTH_MODE</label>
								<button
									type="button"
									onClick={() => setHideInHome(!hideInHome)}
									className={`relative inline-flex h-5 w-10 items-center rounded-none transition-colors border ${
										hideInHome ? 'bg-red-500/20 border-red-500' : 'bg-neutral-800 border-neutral-700'
									}`}
								>
									<span
										className={`inline-block h-3 w-3 transform rounded-none transition-transform ${
											hideInHome ? 'translate-x-6 bg-red-500' : 'translate-x-1 bg-neutral-500'
										}`}
									/>
								</button>
							</div>
							<div className="flex justify-end space-x-3 pt-4">
								<button
									type="button"
									onClick={() => setShowPersonEditModal(false)}
									className={`px-4 py-2 border font-mono text-[10px] tracking-widest transition-colors ${isDark ? 'border-[#333] text-neutral-500 hover:bg-[#222]' : 'bg-neutral-100 text-neutral-600 border-neutral-300 hover:bg-neutral-200'}`}
								>
									ABORT
								</button>
								<button
									type="submit"
									className="px-6 py-2 border border-emerald-500/50 bg-emerald-500/10 text-emerald-500 font-mono text-[10px] tracking-[0.2em] font-bold hover:bg-emerald-500 hover:text-black transition-all"
								>
									EXEC: UPDATE
								</button>
							</div>
						</form>
					</div>
				</div>,
				document.body
			)}

			{/* Lightbox Modal */}
			{lightboxImage && createPortal(
				<div
					className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-[1000] p-4"
					onClick={() => {
						setLightboxImage(null);
						setIsAvatarLightbox(false);
						setLightboxMedia(null);
						setZoomLevel(1);
						setImagePosition({ x: 0, y: 0 });
					}}
					onWheel={(e) => {
						e.preventDefault();
						const delta = e.deltaY > 0 ? -0.1 : 0.1;
						setZoomLevel(prev => Math.max(0.5, Math.min(5, prev + delta)));
					}}
					onMouseMove={(e) => {
						if (isDragging && zoomLevel > 1) {
							setImagePosition({
								x: e.clientX - dragStart.x,
								y: e.clientY - dragStart.y
							});
						}
					}}
					onMouseUp={() => setIsDragging(false)}
					onMouseLeave={() => setIsDragging(false)}
				>
					{/* Download and Close Buttons */}
					<div className="absolute top-4 right-4 flex items-center space-x-3 z-10">
						<button
							onClick={async (e) => {
								e.stopPropagation();
								try {
									const response = await fetch(lightboxImage);
									const blob = await response.blob();
									const url = window.URL.createObjectURL(blob);
									const link = document.createElement('a');
									link.href = url;
									const fileName = isAvatarLightbox 
										? `avatar-${person?.person_name}-${person?.person_surname}.${blob.type.includes('png') ? 'png' : 'jpg'}`
										: (lightboxMedia?.sm_fileName || 'media');
									link.download = fileName;
									document.body.appendChild(link);
									link.click();
									document.body.removeChild(link);
									window.URL.revokeObjectURL(url);
								} catch (error) {
									console.error('Download error:', error);
								}
							}}
							className={`p-2 border transition-all ${isDark ? 'bg-black/80 border-neutral-800 text-neutral-400 hover:text-white hover:border-emerald-500/50' : 'bg-white border-neutral-300 text-neutral-600 hover:text-black hover:border-emerald-500/50'}`}
							title="Download"
						>
							<Download className="w-5 h-5" />
						</button>
						<button
							onClick={(e) => {
								e.stopPropagation();
								setLightboxImage(null);
								setIsAvatarLightbox(false);
								setLightboxMedia(null);
								setZoomLevel(1);
								setImagePosition({ x: 0, y: 0 });
							}}
							className={`p-2 border transition-all ${isDark ? 'bg-black/80 border-neutral-800 text-neutral-400 hover:text-white hover:border-emerald-500/50' : 'bg-white border-neutral-300 text-neutral-600 hover:text-black hover:border-emerald-500/50'}`}
							title="Close"
						>
							<X className="w-5 h-5" />
						</button>
					</div>
					{lightboxType === 'video' ? (
						<video
							src={lightboxImage}
							className="max-w-[90vw] max-h-[90vh] object-contain pointer-events-auto"
							controls
							autoPlay
							onClick={(e) => e.stopPropagation()}
						/>
					) : (
						<img
							src={lightboxImage}
							alt="Lightbox"
							className={`${isAvatarLightbox ? "max-w-[90vw] h-full object-contain" : "max-w-[90vw] max-h-[90vh] object-contain"} pointer-events-auto`}
							style={{
								transform: `scale(${zoomLevel}) translate(${imagePosition.x / zoomLevel}px, ${imagePosition.y / zoomLevel}px)`,
								transformOrigin: 'center center',
								cursor: isDragging ? 'grabbing' : (zoomLevel > 1 ? 'grab' : 'default'),
								transition: isDragging ? 'none' : 'transform 0.1s'
							}}
							onClick={(e) => e.stopPropagation()}
							onMouseDown={(e) => {
								if (e.button === 0 && zoomLevel > 1) {
									e.stopPropagation();
									setIsDragging(true);
									setDragStart({ x: e.clientX - imagePosition.x, y: e.clientY - imagePosition.y });
								}
							}}
							draggable={false}
						/>
					)}
				</div>,
				document.body
			)}
		
		{/* Last Update */}
		{lastUpdate && (
			<div className="text-right mt-4">
				<p className={`text-xs italic ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
					Last Update: {lastUpdate}
				</p>
			</div>
		)}
		</div>
	);
};

export default PersonDetail;

